/**
 * CRM shared types
 *
 * Mirrors the backend response shapes from routes/crm.js. `screener` is the
 * label-resolved view built server-side (crmScreener.js); `answers` is the raw
 * typed lead_answers (kept for back-compat).
 */
import type { Brief } from './brief/types';

/** A recruiter-composed SMS/email queued for a future send (Schedule-send).
 *  Mirrors crm.scheduled_messages; fired by the crm-worker tick. */
export interface ScheduledMessage {
  id: string;
  network_id: string;
  contact_id: string;
  channel: 'sms' | 'email';
  subject: string | null;
  body: string;
  study_id: string | null;
  study_document_ids: string[];
  send_at: string;
  status: 'scheduled' | 'sending' | 'sent' | 'failed' | 'canceled';
  attempts: number;
  sent_at: string | null;
  communication_id: string | null;
  /** Breadcrumb: block reason / send error / 'deferred:<iso>' (quiet hours). */
  last_error: string | null;
  created_by: string | null;
  canceled_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Contact {
  id: string;
  network_id?: string;
  site_id?: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  phone_e164: string | null;
  email: string | null;
  /** Recruiter-entered date of birth, 'YYYY-MM-DD' (lead header). NULL = not recorded. */
  date_of_birth?: string | null;
  lifecycle_status: string | null;
  best_eligibility: string | null;
  /** Best landing-form eligibility verdict across this lead's studies,
   *  denormalized for filtering/sorting (eligible|needs_review|pending|
   *  not_eligible). Same field the inbox eligibility filter uses. */
  incoming_eligibility?: string | null;
  owner_user_id?: string | null;
  /** The one study this patient is being considered for (the chip under their
   *  name + the Call Mode default). Auto-set when a prescreen starts; editable. */
  primary_study_id?: string | null;
  tags?: string[];
  sms_opt_out: boolean;
  /** Conversation state (denormalized from communications by a DB trigger). */
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
  last_activity_at?: string | null;
  /** Recruiter archived this conversation (an inbound reply auto-reopens it). */
  archived_at?: string | null;
  /** Worklist state (denormalized from communications by a DB trigger). */
  needs_reply_at?: string | null;
  /** Outbound attempts since the lead last engaged (resets on any inbound). */
  attempt_count?: number;
  /** When the next outreach attempt is due (cadence engine); null = paused. */
  next_action_at?: string | null;
  stage_changed_at?: string | null;
  /** Recruiter snoozed this lead off the working lists until this moment (#6).
   *  NULL = active; a future value hides it from EVERY working list — worklist
   *  buckets (incl. needs_reply), inbox, board, conversations — and it
   *  auto-resurfaces when the time passes. A new inbound message clears the
   *  snooze DB-side, so a patient who writes back is never left hidden.
   *  Roster views (By Study, patient database) are not filtered. */
  snoozed_until?: string | null;
  /** Set while the lead is an open member of a launching/active outreach list
   *  (docs/crm-outreach-lists-plan.md §B2). The lead page shows the list chip only
   *  when this is set, so leads on no list never pay for the lookup. */
  active_outreach_list_id?: string | null;
  created_at: string;
  updated_at?: string;
}

/** Conversation-state filter for GET /contacts (?conversation=). */
export type ConversationFilter = 'active' | 'awaiting' | 'needs_reply' | 'archived' | 'agent';

/** Per-contact study link summary returned on the inbox list. */
export interface StudyLink {
  study_slug: string | null;
  /** Public study name frozen on the lead snapshot at intake. */
  study_title: string | null;
  /** Recruiter-only internal alias overlaid live (null = none); render
   *  `study_internal_title || study_title` in recruiter views. */
  study_internal_title?: string | null;
  lead_source: string | null;
  eligibility_status: string | null;
  first_seen_at: string | null;
  /**
   * How the lead row got here ('native', 'wcr_webhook', 'manual', 'search', ...). A 'search' row
   * is a Search export moving an old lead, never an arrival (the attention tiers' "hot" rule).
   * Present on the worklist, safety-net and recruitment-list reads; absent elsewhere.
   */
  origin?: string | null;
}

/**
 * When the lead last ARRIVED: created, or a later form submission (a re-submission is someone
 * raising their hand again); a Search export's lead row never counts. Mirrors backend
 * services/crmAttention.js arrivedAtMs, which decides who is a hot new lead.
 */
export function leadArrivedAt(c: { created_at?: string | null; studies?: StudyLink[] | null }): string | null {
  let best = c.created_at || null;
  let bestMs = best ? Date.parse(best) : NaN;
  for (const s of c.studies || []) {
    if (!s || s.origin === 'search' || !s.first_seen_at) continue;
    const t = Date.parse(s.first_seen_at);
    if (Number.isFinite(t) && (!Number.isFinite(bestMs) || t > bestMs)) {
      best = s.first_seen_at;
      bestMs = t;
    }
  }
  return best;
}

/** Most recent communication, embedded on the inbox list for triage. */
export interface LastComm {
  channel: string;
  direction: string | null;
  status: string | null;
  body: string | null;
  occurred_at: string;
}

/**
 * The lead's own newest inbound text, email or call (the body cut at 300 characters). Only the
 * worklist's Needs reply and Keep the conversation going rows carry it: there `last_comm` can be a
 * note, an automated text or our own nudge that landed after the lead's message.
 */
export interface LastReply {
  channel: string;
  status: string | null;
  body: string | null;
  occurred_at: string;
}

/**
 * The single most pertinent "where is this lead now" signal, derived server-side
 * (crmMilestone.deriveMilestone) and rendered under the lifecycle badge in the
 * inbox / conversations / board lists. `key` is the semantic milestone (the UI
 * owns its label, icon, and tone); `at` carries the callback time for the
 * call_scheduled / call_overdue keys (null otherwise).
 */
export type MilestoneKey =
  | 'needs_reply'
  | 'call_scheduled'
  | 'call_overdue'
  | 'appt_scheduled'
  | 'needs_first_contact'
  | 'follow_up_due'
  | 'awaiting_records'
  | 'ready_to_schedule'
  | 'going_stale';

export interface Milestone {
  key: MilestoneKey;
  at: string | null;
}

/** Row shape of GET /api/crm/contacts (enriched inbox payload). */
export interface ContactListItem extends Contact {
  studies: StudyLink[];
  /**
   * The lead's AI brief (docs/plans/crm-brief-plan.md B3.7): set on the worklist's tier-1 rows only
   * while briefs are on and the lead has a fresh one. Absent = render exactly as before.
   */
  brief?: Brief | null;
  last_comm: LastComm | null;
  /** Two worklist buckets only (see LastReply); null = no inbound on record, absent = not read. */
  last_reply?: LastReply | null;
  /** Derived pertinent milestone (null when none applies — see Milestone). */
  milestone?: Milestone | null;
  /**
   * Study-list memberships (multi-study lead lists). Present when the backend
   * embeds them; a By-Study section renders the status for ITS study from here
   * rather than the contact-level lifecycle_status. Absent for lead-only study
   * slugs, which have no crm.studies row and so cannot have memberships.
   */
  memberships?: ContactRowMembership[];
  /**
   * EVERY study this lead is randomized in, regardless of which study the list
   * is filtered to (empty array when none). A separate batched attachment, NOT
   * part of the `memberships` embed above: that embed is scoped to the filtered
   * study, so it can never answer "randomized somewhere ELSE" — which is exactly
   * what a By-Study row has to warn about. Present only under a study scope.
   */
  randomized_memberships?: ContactRandomizedMembership[];
  /**
   * The landing-form verdict for the study (or studies) the list is scoped to:
   * the best eligibility_status of THIS lead's submissions for that study,
   * null when they never filled out its form (added by hand, moved over, put on
   * the list by an outreach or Search export). Present only on a study-scoped
   * read (GET /contacts?study=, the recruitment list's rows). A surface about
   * one study renders THIS, never `incoming_eligibility`, which is the best
   * verdict across every study's form (owner, 2026-09-28; backend
   * services/crmStudyEligibility.js).
   */
  study_eligibility?: string | null;
  /**
   * Set ONLY when the viewer holds this row because its owner is out of office
   * today. Ownership is NOT transferred — render a CoverageBadge whenever it is
   * present, because a covered row is otherwise indistinguishable from your own
   * and a recruiter will assume it was reassigned to them.
   */
  coverage?: CoverageRef | null;
}

/**
 * One entry of the `randomized_memberships` attachment: a study this lead is
 * randomized in, carrying enough of the study to name it on the row. `slug`/
 * `title` are nullable like ContactStudyMembership's — the endpoint substitutes
 * a bare {id} stub if the embed comes back empty, so a reader must not assume
 * either is present.
 */
export interface ContactRandomizedMembership {
  study_id: string;
  status_changed_at: string;
  study: { id: string; slug: string | null; title: string | null };
}

/**
 * One row of GET /api/crm/contacts/:id/studies (and the `memberships` embed on
 * the contact detail): a study this lead has EVER been considered for, with
 * their status ON THAT STUDY. Multi-study lead lists — a lead can hold several
 * of these at once, and a pivot never removes one.
 */
export interface ContactStudyMembership {
  id: string;
  study: { id: string; slug: string | null; title: string | null };
  /** Contact lifecycle vocabulary + the membership-only completed/discontinued. */
  status: string;
  status_changed_at: string;
  status_changed_by?: string | null;
  /** lead | recruiter | pivot | form | import | ctms | agent | backfill | trigger */
  source: string;
  first_considered_at: string;
  metadata: { randomized_conflict?: { study_id: string; at: string } } & Record<string, unknown>;
  /**
   * This study's recruiter (migration 20260929190000): a lead on two studies can
   * have two. On the focus study it equals contact.owner_user_id. Absent from an
   * API that predates the migration.
   */
  owner_user_id?: string | null;
  /** True when this study is the lead's current primary_study_id. */
  is_focus: boolean;
}

/** The per-study slice of a membership that rides GET /api/crm/contacts rows. */
export interface ContactRowMembership {
  study_id: string;
  status: string;
  status_changed_at: string;
  first_considered_at: string;
  /** This study's recruiter (20260929190000); absent from an older API. */
  owner_user_id?: string | null;
}

/**
 * A future-study interest list (GET /api/crm/interest-categories): a standard
 * study type the site regularly runs (T2DM no injectables, migraine, gout, …).
 * Not a study — the catalog exists precisely because the study doesn't yet.
 */
export interface InterestCategory {
  id: string;
  key: string;
  label: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  /** Live active-member count; present only when requested (?counts=true). */
  member_count?: number;
}

/** One row of GET /api/crm/contacts/:id/interests — an ACTIVE list membership. */
export interface ContactInterest {
  id: string;
  contact_id: string;
  category_id: string;
  note: string | null;
  /** recruiter | disposition */
  source: string;
  added_by: string | null;
  created_at: string;
  removed_at: string | null;
  removed_by: string | null;
  category: InterestCategory | null;
}

/** GET /api/crm/team — assignable recruiters/staff for the CRM network. */
export interface TeamMember {
  id: string;
  name: string;
  email: string | null;
  role?: string | null;
  /** True when this member has a live out-of-office day covering today. */
  out_today?: boolean;
  /** Inclusive last day of that absence (YYYY-MM-DD) — the "back Monday" hint. */
  out_until?: string | null;
}

/**
 * GET /api/crm/studies row — CRM studies merged with lead counts by slug.
 * source 'crm' = a crm.studies row (id set); source 'leads' = seen in lead
 * data only (id null) — the hub offers adoption for those.
 */
export interface StudyOption {
  slug: string;
  title: string | null;
  /** Admin-only internal title (recruiter views); falls back to title. Null for
   *  lead-only stubs. */
  internal_title?: string | null;
  count: number;
  id?: string | null;
  /** draft | recruiting | paused | closed (null for lead-only stubs). */
  status?: string | null;
  /** When an admin archived the study (hidden from the default list). */
  archived_at?: string | null;
  source?: 'crm' | 'leads';
  sponsor_name?: string | null;
  protocol_number?: string | null;
  /** Registry link (public.protocols id) — null = unlinked legacy study. */
  protocol_id?: string | null;
  therapeutic_area?: string | null;
  stipend_summary?: StipendSummary | null;
  /** Active published prescreen version (null = none yet). */
  prescreen_version?: number | null;
  /** Live on the public WCR website (the "Show on website" toggle). */
  show_on_website?: boolean;
  is_featured?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * GET /api/crm/study-lead-stats?study=<slug> — exact per-lifecycle lead counts
 * for one study (the by-study view). `by_status` has every canonical lifecycle
 * key (zeros kept); `considering` is the active-funnel roll-up.
 */
export interface StudyLeadStats {
  study: string;
  /**
   * Every canonical lifecycle key (zeros kept) PLUS the membership-only
   * completed/discontinued. Under the membership read path these count
   * everyone ever considered for the study, bucketed by their status on it.
   */
  by_status: Record<string, number>;
  /** Everyone ever considered for this study (not just the active funnel). */
  total: number;
  considering: number;
}

/**
 * Result of POST /api/crm/studies/import-leads — the per-upload tally.
 * created + merged + flagged + duplicate_in_file + invalid === total.
 */
export interface LeadImportSummary {
  total: number;
  /** New contacts created. */
  created: number;
  /** Linked to an existing patient (merged by phone/email). */
  merged: number;
  /** Matched but conflicting (phone vs email point at different people) — review. */
  flagged: number;
  /** Same phone appeared earlier in the file. */
  duplicate_in_file: number;
  /**
   * Leads placed on this study's list who are STILL randomized in another
   * study (multi-study lead lists). A bulk import is an automated path — it is
   * never blocked by the guard, so these are reported for follow-up instead.
   */
  randomized_conflicts?: Array<{
    contact_id: string;
    name: string | null;
    randomized_study_slug: string | null;
    randomized_study_title: string | null;
  }>;
  /** Rows that couldn't be imported (bad/missing phone, etc.). */
  invalid: number;
  errors: { row: number; error: string }[];
}

// ---------------------------------------------------------------------------
// Study Hub (Expansion Phase A)
// ---------------------------------------------------------------------------

/** Live stipend/compensation summary on a study (human-edited; AI-publishable). */
export interface StipendSummary {
  amount_text?: string | null;
  schedule_text?: string | null;
  notes?: string | null;
}

export interface Study {
  id: string;
  slug: string;
  /** Public-facing study name: drives the slug, the WCR public website, and the
   *  {{study_title}} SMS/email merge. Patient-visible. */
  title: string;
  /** Admin-only internal/working title shown in recruiter CRM views; falls back
   *  to `title` when null. NEVER public (no slug, website, or merge use). */
  internal_title: string | null;
  sponsor_name: string | null;
  protocol_number: string | null;
  /** Registry link (public.protocols id) — the eReg source-of-truth record.
   *  Required on new studies while registry reads are on; null = unlinked
   *  legacy study (shows the "Link to eReg" banner on StudyDetail). */
  protocol_id?: string | null;
  therapeutic_area: string | null;
  /** draft | recruiting | paused | closed. */
  status: string;
  stipend_summary: StipendSummary;
  /** Lead count by slug (embedded on the detail response). */
  count?: number;
  // Public website ("Show on website") — marketing/SEO copy + the publish gate.
  // Drive the WCR public studies listing + each study's information page.
  show_on_website?: boolean;
  is_featured?: boolean;
  condition?: string | null;
  compensation_text?: string | null;
  short_description?: string | null;
  full_description?: string | null;
  requirements_summary?: string[] | null;
  age_range?: string | null;
  study_duration?: string | null;
  visit_count?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  /** The one published form the ad landing page (/study/<slug>) serves. */
  landing_form_id?: string | null;
  /** Item #05: the one published questionnaire texted to leads (respond-options). */
  questionnaire_form_id?: string | null;
  // Clinician portal (P2) — the CONFIDENTIAL study directory shown to referring
  // clinicians. Distinct from the public-website fields above on purpose:
  // listing here never publishes to the public site.
  show_on_portal?: boolean;
  portal_synopsis?: string | null;
  /** Target enrollment / timeline notes — clinician-facing. */
  portal_enrollment_notes?: string | null;
  /** What I/E criteria clinicians may see (O-12; default closed). */
  portal_ie_disclosure?: 'none' | 'summary' | 'full' | null;
  /** The pinned confirmed review-criteria set served when disclosure is 'full'. */
  portal_ie_criteria_set_id?: string | null;
  /** Where the sponsor's permission is recorded (CDA clause, email date, CTA §). */
  portal_ie_sponsor_ref?: string | null;
  portal_show_sponsor?: boolean;
  /** Provenance of the disclosure decision — rendered as returned (ids stay ids). */
  portal_ie_approved_by?: string | null;
  portal_ie_approved_at?: string | null;
  /**
   * Per-study JSONB config blob (study detail returns the full row). Holds the
   * setup-checklist draft and the item #01 ineligible-CTA toggle, among others.
   */
  config?: {
    ineligible_cta?: { enabled?: boolean } | null;
    /** Same-URL landing A/B: weighted-random assignment among the study's
     *  active landing variants when a /study/<slug> arrival has no ?v=. */
    landing_experiment?: { enabled?: boolean } | null;
    /** Per-study opt-out of the generic network-wide welcome flow (Flows tab). */
    suppress_generic_welcome?: boolean;
    /** CTMS (CRIO) mapping: links this study to a CRIO study id (+ optional site override). */
    crio?: { study_id?: string | null; site_id?: string | null } | null;
    [key: string]: unknown;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface StudyDocument {
  id: string;
  /** protocol | icf | amendment | other. */
  doc_type: string;
  filename: string;
  mime_type: string | null;
  file_size: number | null;
  page_count: number | null;
  version_label: string | null;
  /** pending | processing | complete | failed. */
  extraction_status: string;
  extraction_error: string | null;
  created_at: string;
  updated_at?: string;
  /** 'registry' = a CURRENT eReg ICF served through the CRM proxy for linked
   *  studies (composer attach picker); absent = legacy crm.study_documents row. */
  source?: 'registry';
}

export type ArtifactType =
  | 'summary' | 'recruiter_script' | 'prescreen_draft' | 'stipend_info' | 'website_content' | 'message_templates'
  // Texting-agent knowledge brief (agent plan §8.1): generation is explicit-only
  // (never part of the no-types fan-out) and publish is §8.1-validated server-side.
  | 'agent_brief'
  // Form-builder generations: job state + provenance rows whose OUTPUT lands on
  // the crm.forms row named by generation_options.form_id (never reviewed in
  // the artifact workspace — they settle straight to published/failed).
  | 'form_landing_draft' | 'form_questionnaire_draft';

/** A reusable composer reply template (crm.message_templates). study_id null = global. */
export interface MessageTemplate {
  id: string;
  study_id: string | null;
  /** sms | email | any. */
  channel: 'sms' | 'email' | 'any';
  title: string;
  subject: string | null;
  body: string;
  /** Picker tab bucket: welcome | study_details | general. */
  category: 'welcome' | 'study_details' | 'general';
  /** manual | ai. */
  source: 'manual' | 'ai';
  /** active | archived. */
  status: 'active' | 'archived';
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at?: string;
}

/** AI-generated, human-edited recruiter asset. content shape varies by type. */
export interface StudyArtifact {
  id: string;
  artifact_type: ArtifactType;
  version: number;
  /** generating | draft | published | superseded | failed. */
  status: string;
  content: any;
  source_document_id: string | null;
  /** eReg version ids this artifact was generated from (registry reads, §3.5);
   *  null = legacy crm-document source. Drives "source updated" badges. */
  source_version_ids?: string[] | null;
  model_id?: string | null;
  /**
   * Persisted generation knobs. `revised_from` marks a hand-edited copy of an
   * earlier version; `instructions` is the admin's free-text direction for an AI
   * build; `ai_revise` is present only while a draft is being AI-revised (a
   * generating prescreen draft).
   */
  generation_options?: {
    revised_from?: number;
    length?: string;
    instructions?: string;
    ai_revise?: { instructions: string };
    /** Form generations only: the crm.forms row the output is written onto. */
    form_id?: string;
    /** Form generations only: 'landing' | 'questionnaire'. */
    surface?: string;
    /** Present when this version was produced by "Update from new version":
     *  a diff-driven edit of the prior content after an eReg re-version.
     *  `outcome` is set once the worker lands ('updated' | 'no_changes'). */
    source_update?: {
      from_version_ids: string[];
      base_version?: number;
      base_status?: string;
      started_at?: string;
      outcome?: 'updated' | 'no_changes';
      documents?: SourceUpdateDocument[];
    };
  } | null;
  error: string | null;
  generated_at: string | null;
  published_at: string | null;
  created_at: string;
  updated_at?: string;
}

/**
 * A calculated (derived) criterion's spec — its value is computed from OTHER
 * answers, not asked directly. Published inputs map a slot to a frozen
 * question_key; draft inputs map a slot to a sibling criterion's `ref`.
 */
export interface PrescreenCalculation {
  /** age | bmi (see formMeta.CALC_PRESETS). */
  preset: string;
  inputs: Record<string, string>;
}

/** One prescreen question/criterion in a published version. */
export interface PrescreenCriterion {
  id: string;
  /** inclusion | exclusion. */
  criterion_kind: string;
  category: string;
  /** Exact protocol wording (provenance). */
  source_text: string;
  /** Layman's-terms question the recruiter asks. */
  display_question: string;
  question_key: string;
  profile_key: string | null;
  /** boolean | number | choice | text | date | height (ft+in, stored as total inches). */
  answer_type: string;
  options: { value: string; label: string }[] | null;
  /** Eligibility check: a leaf {op,…} or an AND/OR group {combinator,conditions}. */
  rule: any;
  /** Present only on derived criteria; inputs hold frozen question_keys. */
  calculation?: PrescreenCalculation | null;
  /** Conditional display (forms show_if shape); question_key refs are FROZEN
   *  sibling question_keys. Hidden questions are skipped by the runner and
   *  excluded from verdicts. */
  show_if?: FormShowIf | null;
  sort_order: number;
}

/** Draft criterion inside a prescreen_draft artifact (pre-publish, no keys yet). */
export interface DraftCriterion {
  criterion_kind: string;
  category: string;
  source_text: string;
  display_question: string;
  answer_type: string;
  options: { value: string; label: string }[] | null;
  rule: any;
  /** Present only on derived criteria; inputs hold sibling `ref`s. */
  calculation?: PrescreenCalculation | null;
  /** Stable draft-local id wiring calculation inputs across reorders. */
  ref?: string;
  /** Conditional display (forms show_if shape); pre-publish the condition
   *  question_keys hold sibling `ref`s — rewritten to frozen keys at publish. */
  show_if?: FormShowIf | null;
  profile_key_suggestion: string | null;
}

/**
 * One cross-study field tag in the network's vocabulary (crm.profile_keys) —
 * the "code name" attached to a question so its answer is queryable across
 * forms/prescreens (condition.t2dm, med.glp1, bmi, …). Curated in the
 * Field-tag library (/crm/settings/profile-keys); picked in the builders via
 * ProfileKeyPicker.
 */
export interface ProfileKey {
  id: string;
  key: string;
  label: string;
  /** boolean | number | choice | text | date. */
  value_type: string;
  unit: string | null;
  options: { value: string; label: string }[] | null;
  description: string | null;
  /** draft (auto-minted by a prescreen publish, awaiting curation) | active | archived. */
  status: 'draft' | 'active' | 'archived';
  created_at: string;
  updated_at: string;
  /** Present when fetched with ?usage=true — where the tag is referenced at authoring time. */
  usage?: ProfileKeyUsage;
}

export interface ProfileKeyUsage {
  forms: { id: string; title: string; status: string }[];
  studies: { id: string; title: string; slug: string | null }[];
}

/** A tag referenced by a form/prescreen but never registered in the vocabulary. */
export interface UnregisteredProfileKey extends ProfileKeyUsage {
  key: string;
}

/** The study's active published prescreen. */
export interface Prescreen {
  id: string;
  version: number;
  status: string;
  published_at: string;
  /** The prescreen_draft artifact this version was published from (fork source for a hand-edit). */
  source_artifact_id?: string | null;
  criteria: PrescreenCriterion[];
}

/** The linked registry record embedded on the study detail (plan §5.1). */
export interface StudyRegistryInfo {
  protocol_id: string;
  protocol_number: string | null;
  study_nickname: string | null;
  sponsor_name: string | null;
  site: { id: string; name: string } | null;
  /** Registry-level soft-archive stamp — surfaces the §6-E7 "archived in eReg" chip. */
  archived_at: string | null;
  /** Generation-readiness probe over the eReg docs (same probe as the backend
   *  /generate gate): ready = a current protocol/ICF/amendment has complete
   *  text; pending = extraction still in flight. */
  readiness?: { ready: boolean; pending: boolean };
}

/** One document's before/after in a source update (counts only, no text). */
export interface SourceUpdateDocument {
  document_id: string;
  label: string;
  doc_type?: string;
  from_version_number: string | number | null;
  to_version_number: string | number | null;
  is_new?: boolean;
  changed: boolean;
  added?: number | null;
  removed?: number | null;
  unchanged?: number | null;
}

/** POST /api/crm/studies/:id/source-update — what was restamped / queued. */
export interface SourceUpdateResponse {
  ok: boolean;
  documents: SourceUpdateDocument[];
  items: {
    kind: 'artifact' | 'prescreen' | 'form' | 'review_criteria';
    artifact_type: string;
    form_id?: string;
    action: 'queued' | 'restamped' | 'current' | 'skipped';
    reason?: string;
    via?: string;
    artifact_id?: string;
    set_id?: string;
  }[];
  counts: { queued: number; restamped: number; current: number; skipped: number };
}

/** §3.5 staleness roll-up: derived rows whose eReg source versions are superseded. */
export interface StudyStaleness {
  any: boolean;
  artifacts: { id: string; artifact_type: ArtifactType; status: string }[];
  criteria_sets: { id: string; status: string }[];
  prescreen: boolean;
}

/** GET /api/crm/studies/:id — full hub detail. */
export interface StudyDetailResponse {
  study: Study;
  documents: StudyDocument[];
  artifacts: StudyArtifact[];
  prescreen: Prescreen | null;
  /** The §7 rollback flag — false restores the pre-P2 legacy behavior. */
  registry_reads_enabled?: boolean;
  /** Linked registry record (null = unlinked, foreign, or flag off). */
  registry?: StudyRegistryInfo | null;
  /** Null when unlinked / flag off / lookup degraded. */
  staleness?: StudyStaleness | null;
}

// ---------------------------------------------------------------------------
// Study setup checklist (Study Hub onboarding)
// ---------------------------------------------------------------------------

/** How the UI completes a checklist item. */
export interface SetupChecklistAction {
  /** tab = switch study-detail tab; route = navigate; set_status = one-click PATCH; none = manual ack. */
  type: 'tab' | 'route' | 'set_status' | 'none';
  value?: string;
  /** Optional button label (e.g. "Open agent settings"); the UI default otherwise. */
  label?: string;
}

/** One computed checklist item (catalog meta + live status). */
export interface SetupChecklistItem {
  key: string;
  group: string;
  label: string;
  description: string;
  required: boolean;
  action: SetupChecklistAction;
  /** True when the item has a data signal (vs manual-only). */
  autoDetected: boolean;
  status: 'done' | 'in_progress' | 'todo';
  /** 'auto' = detected from data, 'manual' = admin ticked it, null = not done. */
  source: 'auto' | 'manual' | null;
  /** A human explicitly signed off on this done item (stronger than the auto/AI check). */
  verified: boolean;
  /** ISO timestamp of the sign-off, when verified. */
  verifiedAt: string | null;
  /** User id who signed off, when verified (captured for audit). */
  verifiedBy: string | null;
}

/** A stored human-verification sign-off record. */
export interface SetupChecklistVerification {
  at: string;
  by: string | null;
}

/** Persisted draft state living in studies.config.setup_checklist. */
export interface SetupChecklistDraft {
  checks: Record<string, boolean>;
  verified: Record<string, SetupChecklistVerification>;
  completed_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/** GET /api/crm/studies/:id/setup. */
export interface SetupChecklistResponse {
  study: { id: string; slug: string; title: string; status: string };
  groups: { key: string; label: string }[];
  items: SetupChecklistItem[];
  progress: {
    done: number; total: number;
    requiredDone: number; requiredTotal: number;
    verified: number; requiredVerified: number;
  };
  draft: SetupChecklistDraft;
}

// ---------------------------------------------------------------------------
// Scheduling gates — a study's required prescreening milestones + the per-lead
// readiness checklist that warns a recruiter before they schedule too early.
// ---------------------------------------------------------------------------

/** A standard milestone in the catalog (auto = derived from data, not a manual tick). */
export interface ScheduleGateCatalogItem {
  key: string;
  label: string;
  description: string;
  auto: boolean;
}

/** A configured (selected) requirement gate for a study — every one is required. */
export interface ScheduleRequirementGate {
  key: string;
  label: string;
  description: string;
  auto: boolean;
  custom: boolean;
  required: boolean;
}

/** GET/PUT /api/crm/studies/:id/schedule-requirements. */
export interface StudyScheduleRequirements {
  studyId: string;
  gates: ScheduleRequirementGate[];
  raw: { gates: string[]; custom: { key: string; label: string }[] };
  catalog: ScheduleGateCatalogItem[];
  updatedAt: string | null;
  updatedBy: string | null;
}

/** One computed readiness item for a lead (auto-derived or a manual sign-off). */
export interface ScheduleReadinessItem {
  key: string;
  label: string;
  description: string;
  auto: boolean;
  custom: boolean;
  required: boolean;
  done: boolean;
  source: 'auto' | 'manual' | null;
  note: string | null;
  doneAt: string | null;
  doneBy: string | null;
}

/** GET /api/crm/contacts/:id/schedule-gates. */
export interface ScheduleReadiness {
  configured: boolean;
  study_id: string | null;
  items: ScheduleReadinessItem[];
  progress: { done: number; total: number };
  unmet: { key: string; label: string }[];
  allMet: boolean;
}

// ---------------------------------------------------------------------------
// Study attempts + disqualifications (Expansion Phase B)
// ---------------------------------------------------------------------------

/** Saved prescreen answer on an attempt (typed columns, lead_answers shape). */
export interface AttemptAnswer {
  attempt_id?: string;
  question_key: string;
  question_type: string | null;
  value_text: string | null;
  value_num: number | null;
  value_bool: boolean | null;
  value_json: unknown;
  updated_at?: string;
}

/** Structured disqualification (why a lead screened out). */
export interface Disqualification {
  id: string;
  attempt_id?: string | null;
  criterion_id: string | null;
  category: string;
  note: string | null;
  recruiter_user_id: string | null;
  lead_source?: string | null;
  occurred_at: string;
  voided_at?: string | null;
  /** Embedded on the analytics drill-down list. */
  contact?: Pick<Contact, 'id' | 'full_name' | 'first_name' | 'last_name'> | null;
  study_title?: string | null;
  criterion?: Pick<PrescreenCriterion, 'id' | 'display_question' | 'criterion_kind'> | null;
}

/**
 * One resolved landing-form question + the patient's answer (label-resolved by
 * crmScreener.displayAnswer). Shared by the profile screener modal and the
 * Reports DQ drill-down. `kind` mirrors eligibility_reasons for highlighting.
 */
export interface LandingAnswer {
  question: string;
  answer: string;
  /** 'exclude' (disqualifying, red) | 'review' (flagged, yellow) | null. */
  kind?: 'exclude' | 'review' | null;
}

/** One contact-x-study screening journey (GET /contacts/:id/attempts). */
export interface StudyAttempt {
  id: string;
  study_id: string;
  study_title?: string | null;
  study_slug?: string | null;
  prescreen_version_id: string | null;
  /** lead | recruiter | pivot | form. */
  source: string;
  /** screening | eligible | disqualified | withdrew | scheduled | enrolled | completed | discontinued. */
  status: string;
  started_by: string | null;
  started_at: string;
  outcome_at: string | null;
  answers: AttemptAnswer[];
  disqualification: Disqualification | null;
  /** Study-matched landing-form Q&A attached by getAttemptDetail (item #2). */
  landing_answers?: LandingAnswer[];
}

// ---------------------------------------------------------------------------
// Cross-study pivot (Expansion Phase F)
// ---------------------------------------------------------------------------

/** The fact that decided a criterion's verdict (null = nothing known). */
export interface StudyMatchFact {
  value: any;
  /** prescreen | form | lead | profile (Phase G meds/conditions). */
  source: string;
  answered_at: string | null;
}

/** One evaluated criterion inside a study match (sort_order order). */
export interface StudyMatchCriterion {
  id: string;
  display_question: string;
  /** inclusion | exclusion. */
  criterion_kind: string;
  category: string;
  sort_order: number;
  verdict: 'pass' | 'fail' | 'unknown';
  fact: StudyMatchFact | null;
}

/** GET /api/crm/contacts/:id/study-matches — one ranked candidate study. */
export interface StudyMatch {
  study: { id: string; slug: string; title: string; internal_title?: string | null; therapeutic_area: string | null };
  /** True when the contact already has an OPEN screening attempt here. */
  resume: boolean;
  open_attempt_id: string | null;
  passes: number;
  fails: number;
  unknowns: number;
  criteria: StudyMatchCriterion[];
}

/** GET /api/crm/analytics/* aggregate row. */
export interface AnalyticsRow {
  group_key: string;
  group_label: string;
  total: number;
}

/**
 * GET /api/crm/analytics/funnel row (lifecycle per group).
 *
 * TWO counts, and the difference between them is the whole point of the tab:
 *   total   — occupancy. Contacts whose CURRENT lifecycle_status is this one.
 *   reached — cumulative. Contacts that EVER got this far, so a lead that
 *             screened and then randomized still counts under Screened.
 * `reached` is null only for a status the stage ledger cannot rank (a
 * hand-typed lifecycle_status), and undefined against a backend older than
 * 20260818140000 — during a deploy the UI falls back to `total`.
 */
export interface FunnelRow extends AnalyticsRow {
  lifecycle_status: string;
  reached?: number | null;
}

/**
 * Live call state for a recruiter, surfaced by the voice presence store and
 * the Calls dashboard. Present only while the recruiter is on a call right now.
 */
export interface LiveCallState {
  on_call: true;
  direction: 'outbound' | 'inbound' | null;
  contact_first_name: string | null;
  /** ISO timestamp the call connected; the UI ticks duration from here to now. Null on the degraded presence path. */
  started_at: string | null;
}

/** Per-recruiter range-window call statistics (GET /api/crm/analytics/calls). */
export interface RecruiterCallStats {
  total: number;
  outbound: number;
  inbound: number;
  completed: number;
  missed: number;
  talk_time_sec: number;
  avg_sec: number;
  last_call_at: string | null;
}

/** One recruiter row in the Calls dashboard (live state + range stats). */
export interface RecruiterCalls {
  user_id: string;
  name: string;
  live: LiveCallState | null;
  stats: RecruiterCallStats;
}

/** GET /api/crm/analytics/calls — the per-recruiter Calls dashboard payload. */
export interface CallsAnalytics {
  range: { from: string | null; to: string | null };
  recruiters: RecruiterCalls[];
  totals: {
    total: number;
    outbound: number;
    inbound: number;
    completed: number;
    missed: number;
    talk_time_sec: number;
    on_call_now: number;
    recruiters_online: number;
  };
  unattributed: { total: number; talk_time_sec: number };
  /** True when a study filter was applied (live presence stays network-wide). */
  study_scoped?: boolean;
}

/** The four real patient-comm channels surfaced on the live feed. */
export type CommFeedChannel = 'sms' | 'email' | 'call' | 'fax';

/**
 * One row on the live communications board (Reports → Live feed, admin-only).
 * A unified shape across SMS / email / voice call / fax: the patient (name +
 * pipeline status), who handled it, and what was said (`preview`, plus `subject`
 * for email and `duration_sec`/`voicemail` for calls).
 */
export interface CommFeedRow {
  id: string;
  channel: CommFeedChannel;
  direction: 'inbound' | 'outbound' | null;
  status: string | null;
  occurred_at: string;
  /** Staff member who sent/handled it; null for inbound or automated sends. */
  user_id: string | null;
  user_name: string | null;
  contact_id: string | null;
  contact_name: string;
  contact_status: string | null;
  /** Email subject line (email only). */
  subject: string | null;
  /** What was said: SMS/email body, call transcript-or-disposition, fax summary. */
  preview: string;
  duration_sec: number | null;
  voicemail: boolean;
  to_number: string | null;
  from_number: string | null;
  page_count: number | null;
  error: string | null;
}

/** GET /api/crm/analytics/communications — the live communications feed payload. */
export interface CommFeedResponse {
  rows: CommFeedRow[];
  /** Network staff roster for the "filter by user" dropdown. */
  team: { id: string; name: string }[];
  has_more: boolean;
  /** Cursor for the next (older) page — pass back as ?before. */
  next_before: string | null;
  study_scoped?: boolean;
}

/**
 * GET /api/crm/analytics/stage-conversion row — one per group x forward-spine
 * stage. A cohort "reached-stage" funnel (high-water mark) with stage-to-stage
 * conversion % and time-in-stage, on a cohort windowed by first stage event
 * rather than by contacts.created_at as the Funnel tab is.
 */
export interface StageConversionRow extends AnalyticsRow {
  stage: string;
  ordinal: number;
  /** Cohort contacts whose pipeline high-water reached at least this stage. */
  reached: number;
  /** % that converted from the PREVIOUS stage into this one (null at the base). */
  conversion_pct: number | null;
  /** Median / p90 total time-in-stage (completed stints only), in seconds. */
  median_dwell_sec: number | null;
  p90_dwell_sec: number | null;
  /** Cohort members still sitting in this stage (excluded from the dwell percentile). */
  censored: number;
  /** Cohort members who re-entered this stage (pivots/re-screens). */
  reentry: number;
}

/** One recruiter's productivity totals + per-day grid (GET /analytics/productivity). */
export interface ProductivityRecruiter {
  user_id: string;
  name: string | null;
  /** metric -> total over the range. */
  totals: Record<string, number>;
  /** 'YYYY-MM-DD' -> { metric -> n }. */
  by_day: Record<string, Record<string, number>>;
}

/** GET /api/crm/analytics/productivity — per-recruiter x per-day activity rollup. */
export interface ProductivityAnalytics {
  range: { from: string | null; to: string | null };
  /** IANA tz used for day bucketing (default America/New_York). */
  tz: string;
  /** Metric keys in display order (mirrors the backend PRODUCTIVITY_METRICS). */
  metrics: string[];
  /** Dense ascending day axis ('YYYY-MM-DD') present in the data. */
  days: string[];
  recruiters: ProductivityRecruiter[];
  totals: Record<string, number>;
  study_scoped?: boolean;
}

/** One recruiter's disposition counts (GET /analytics/dispositions). */
export interface DispositionRecruiter {
  user_id: string;
  name: string | null;
  /** lifecycle to_status -> distinct leads moved there in range. */
  counts: Record<string, number>;
  /** Sum across all matrix statuses. */
  total: number;
  /** Sum across the negative dispositions only. */
  negative_total: number;
}

/** GET /api/crm/analytics/dispositions — recruiter × disposition matrix. */
export interface DispositionAnalytics {
  range: { from: string | null; to: string | null };
  /** The matrix columns, in display order (positives then negatives). */
  statuses: string[];
  /** The subset of `statuses` that are terminal-negative (reason-drillable). */
  negative_statuses: string[];
  recruiters: DispositionRecruiter[];
  totals: Record<string, number>;
  grand_total: number;
  study_scoped?: boolean;
}

/** GET /api/crm/analytics/dispositions/reasons row. */
export interface DispositionReasonRow {
  reason_code: string;
  total: number;
}

/** GET /api/crm/analytics/dispositions/reason-contacts row (one lead). */
export interface DispositionContact {
  contact_id: string;
  contact: Pick<Contact, 'id' | 'full_name' | 'first_name' | 'last_name'> | null;
  reason_code: string;
  reason_note: string | null;
  source: string;
  occurred_at: string;
}

/** GET /api/crm/analytics/screening/:gate/verdicts row. */
export interface ScreeningVerdictRow {
  /** eligible | needs_review | not_eligible | pending | incomplete | unknown. */
  verdict: string;
  total: number;
}

/** GET /api/crm/analytics/screening/:gate/reasons row — one driver. */
export interface ScreeningReasonRow {
  question_key: string;
  /** The question text (stored on the reason). */
  label: string;
  /** 'exclude' (drove not-eligible) | 'review' (drove needs-review). */
  kind: string;
  /** The answer that triggered it (Yes/No, a number, text, or '(no answer)'). */
  answer_value: string;
  total: number;
}

/** GET …/screening/:gate/daily row — leads on one local day in one verdict. */
export interface ScreeningDailyRow {
  /** 'YYYY-MM-DD' (bucketed in the report tz). */
  day: string;
  verdict: string;
  total: number;
}

/** GET …/screening/intake/sources row — landing leads by source x verdict. */
export interface ScreeningSourceRow {
  lead_source: string;
  verdict: string;
  total: number;
}

/** One lead behind a driver (GET …/reason-contacts). The answer is PHI (audited). */
export interface ScreeningReasonContact {
  contact_id: string;
  contact: Pick<Contact, 'id' | 'full_name' | 'first_name' | 'last_name'> | null;
  answer_value: string;
  occurred_at: string;
}

/** GET /api/crm/analytics/sales-summary — headline KPIs for one creation-cohort. */
export interface SalesSummary {
  leads: number;
  reached: number;
  reach_pct: number | null;
  speed_median_sec: number | null;
  speed_p90_sec: number | null;
  speed_within_15min_pct: number | null;
  outbound_calls: number;
  connected_calls: number;
  connect_pct: number | null;
  randomized: number;
  lead_to_rand_pct: number | null;
  cycle_median_days: number | null;
}

/**
 * GET /api/crm/analytics/recruiter-speed (owner request 2026-10-01): how long a
 * PERSON took to reach an eligible or needs-review lead once it was the team's
 * turn. Both measures ride every response; the page's toggle picks one.
 */
export type SpeedMeasure = 'business' | 'clock';
export type SpeedThreshold = '15m' | '1h' | '4h' | '1d';

export interface SpeedMeasureStats {
  median_sec: number | null;
  p90_sec: number | null;
  /** The longest current wait among leads nobody has reached yet. */
  oldest_wait_sec: number | null;
  /** `of` = leads whose outcome at that threshold is known (reached, or still
   *  unreached once it passed); `hit` = reached within it. */
  within: Record<SpeedThreshold, { hit: number; of: number }>;
}

export interface SpeedStats {
  /** reached + waiting + closed: the leads whose clock has started. */
  leads: number;
  reached: number;
  /** Reached before the clock started (e.g. called while the agent was still screening). */
  reached_early: number;
  waiting: number;
  /** Not reached, and since closed (archived or a closed stage). */
  closed: number;
  /** The texting agent took them and never handed them to the team (open, or closed without a hand-off). */
  with_agent: number;
  /** They booked a call time that has not come yet. */
  booked_later: number;
  business: SpeedMeasureStats;
  clock: SpeedMeasureStats;
}

export interface RecruiterSpeedRow extends SpeedStats {
  /** User id or study id; null = unassigned / a form with no CRM study. */
  key: string | null;
  name: string | null;
}

export interface RecruiterSpeed {
  hours: { label: string; timezone: string; day_seconds: number; closed_dates: number };
  rejoin_hours: number;
  thresholds: SpeedThreshold[];
  range: { from: string | null; to: string | null };
  study_scoped: boolean;
  overall: SpeedStats;
  by_path: Array<SpeedStats & { key: 'web' | 'agent' }>;
  by_recruiter: RecruiterSpeedRow[];
  by_study: RecruiterSpeedRow[];
}

/** GET /api/crm/contacts/stats — pipeline counts for the inbox tabs. */
export interface LifecycleStats {
  total: number;
  unassigned: number;
  by_status: Record<string, number>;
  /** Conversation-tab counts (sidebar badges). */
  conversations?: {
    active: number;
    awaiting: number;
    needs_reply: number;
    archived: number;
    /** Not served today — the agent view derives its list live (M-15). */
    agent?: number;
  };
  /** Unmatched inbound faxes awaiting triage (Records sidebar badge, Phase E). */
  fax_needs_match?: number;
  /** Unheard voicemails (Phone sidebar badge, Phase 4c). */
  voicemail_unheard?: number;
}

// ---- Phone tab (Phase 4c) ---------------------------------------------------

/** A voicemail row for the Phone tab inbox (GET /api/crm/voice/voicemails). */
export interface Voicemail {
  id: string;
  contact_id: string | null;
  name: string | null;
  from: string | null;
  duration_sec: number | null;
  occurred_at: string;
  heard_at: string | null;
  heard_by: string | null;
}

/** A recent call row for the Phone activity log (GET /api/crm/voice/calls). */
export interface CallActivity {
  id: string;
  contact_id: string | null;
  name: string | null;
  direction: 'inbound' | 'outbound' | string;
  status: string | null;
  occurred_at: string;
  from: string | null;
  /** Recruiter who placed (outbound) or answered (inbound) the call, if known. */
  agent: string | null;
  duration_sec: number | null;
  voicemail: boolean;
}

/** One open-office-hours schedule (after-hours auto-text, item #3). */
export interface AfterHoursSchedule {
  /** Open weekdays, 0=Sun..6=Sat. */
  days: number[];
  /** Open time, 'HH:MM' (24h). */
  open: string;
  /** Close time, 'HH:MM' (24h). */
  close: string;
}

/** Editable phone settings (GET/PUT /api/crm/voice/settings). */
export interface VoiceSettings {
  greeting: string;
  /** True when a staff-recorded greeting is stored (played instead of TTS). */
  has_greeting_audio: boolean;
  hold_music_url: string;
  owner_ring_sec: number;
  group_ring_sec: number;
  voicemail_max_sec: number;
  voicemailEnabled: boolean;
  callTransferEnabled: boolean;
  /** After-hours auto-text settings (item #3). Dormant until `enabled`. */
  after_hours?: {
    enabled: boolean;
    default: AfterHoursSchedule;
    sites: Record<string, AfterHoursSchedule>;
    message: string;
  };
}

// ---- Records release + e-sign + fax (Expansion Phase E) ---------------------

/** Present while a busy/no-answer fax is auto-retrying (queued/sending, attempt > 1). */
export interface FaxAutoRetry {
  attempt: number;
  max: number;
}

/** Latest outbound fax for a signature request (RecordsCard). */
export interface RecordsFax {
  id: string;
  /** queued | sending | delivered | failed. */
  status: string;
  to_number: string | null;
  error: string | null;
  page_count: number | null;
  updated_at: string;
  /** Non-null while the system is auto-retrying a transient failure (e.g. busy line). */
  auto_retry?: FaxAutoRetry | null;
}

/** Latest outbound fax to ONE office for a request (Phase E.2 — multi-office). */
export interface RecordsOfficeFax {
  to_number: string | null;
  name: string | null;
  /** queued | sending | delivered | failed. */
  status: string;
  error: string | null;
  updated_at: string | null;
  /** Non-null while the system is auto-retrying a transient failure (e.g. busy line). */
  auto_retry?: FaxAutoRetry | null;
}

/** One row of GET /api/crm/contacts/:id/records-requests. */
export interface RecordsRequest {
  id: string;
  /** created | sent | viewed | signed | expired | cancelled (lazily expired). */
  status: string;
  created_at: string;
  expires_at: string;
  sent_at: string | null;
  viewed_at: string | null;
  signed_at: string | null;
  signer_name: string | null;
  /** Null until the patient names the office(s); then a first-office summary. */
  doctor_name: string | null;
  doctor_fax: string | null;
  /** Recruiter-chosen records timespan (6m/12m/3y/5y/all) + its label. */
  lookback: string | null;
  lookback_label: string | null;
  /** What this request asks each office for, chosen at fax time and printed on
   * the fax cover sheet (the signed release is hash-locked, so it can't carry
   * this). Always resolved by the server — an untouched request comes back with
   * the default record types. */
  records_scope: { types: string[]; note: string };
  /** The office(s) the patient named (empty until signed); fax/location added
   * by the recruiter at send time. */
  offices: { name: string; city_state: string; fax: string | null }[];
  hospital: { name: string; city_state: string; fax: string | null } | null;
  pharmacy: { name: string; city_state: string; fax: string | null } | null;
  document_id: string | null;
  signed_document_id: string | null;
  /** The overall latest outbound fax (back-compat); per-office statuses in `faxes`. */
  fax: RecordsFax | null;
  faxes: RecordsOfficeFax[];
  received: { document_id: string; filename: string; created_at: string }[];
}

/** One outbound fax transmission for a contact, as the fax delivery report
 * picker sees it (GET /contacts/:id/faxes). Recipient + sender + document are
 * resolved server-side; delivered_at is the carrier-confirmed moment. */
export interface ContactFax {
  id: string;
  /** queued | sending | delivered | failed */
  status: string;
  status_label: string;
  created_at: string;
  delivered_at: string | null;
  updated_at: string;
  to_number: string | null;
  to_number_display: string;
  from_number: string | null;
  from_number_display: string;
  recipient_name: string | null;
  recipient_location: string | null;
  reference_code: string | null;
  page_count: number | null;
  error: string | null;
  /** 1 for a first send; >1 for an automatic retry after a busy line. */
  attempt: number;
  retry_of: string | null;
  is_standalone: boolean;
  note: string | null;
  document_id: string | null;
  document_filename: string | null;
  document_type: string | null;
  sent_by_user_id: string | null;
  sent_by: string | null;
  signature_request_id: string | null;
  release_signed_at: string | null;
  release_signer_name: string | null;
  patient_dob: string | null;
}

/** A per-contact PHI file (crm.documents). Medical records shown on the lead
 * profile are doc_type 'medical_record': faxed in (source 'fax_inbound'),
 * dropped straight onto the profile by a recruiter (source 'upload'), or
 * combined from several records (source 'merged'). */
export interface ContactDocument {
  id: string;
  /** release_unsigned | release_signed | medical_record | cover_sheet | other. */
  doc_type: string;
  filename: string;
  mime_type: string | null;
  file_size: number | null;
  /** generated | esign | fax_inbound | sms_inbound | upload | merged. */
  source: string;
  created_at: string;
}

/** Derived delivery stats for a provider office (from crm.fax_jobs by fax). */
export interface ProviderOfficeStats {
  sent: number;
  delivered: number;
  failed: number;
  last_faxed_at: string | null;
}

/** One row of GET /api/crm/provider-offices — the reusable fax directory:
 * an office, the doctors who practice there, ONE fax, location, notes, a manual
 * 1-5 rating, and derived fax-delivery stats. */
export interface ProviderOffice {
  id: string;
  network_id: string;
  office_name: string;
  doctor_names: string[];
  fax_number: string | null;
  location: string | null;
  notes: string | null;
  /** Manual 1-5 reliability rating (null = unrated). */
  rating: number | null;
  created_at: string;
  updated_at: string;
  stats: ProviderOfficeStats;
}

/** GET /api/crm/fax/inbox row (triage queue). */
export interface FaxInboxJob {
  id: string;
  from_number: string | null;
  to_number: string | null;
  page_count: number | null;
  created_at: string;
  document: { id: string; filename: string; file_size: number | null } | null;
  candidates: { id: string; name: string; phone: string | null }[];
}

/** GET /api/crm/fax row — one fax (either direction) in the history list. */
export interface FaxRecord {
  id: string;
  direction: 'outbound' | 'inbound';
  /** outbound: queued|sending|delivered|failed; inbound: received|needs_match|matched|discarded */
  status: string;
  to_number: string | null;
  from_number: string | null;
  reference_code: string | null;
  page_count: number | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  contact: { id: string; name: string } | null;
  /** The viewable PDF (inbound media, or the outbound merged cover+doc). */
  document_id: string | null;
  document_filename: string | null;
  is_standalone: boolean;
  note: string | null;
}

/** GET /api/crm/fax/config — the live sending fax number, for display only. */
export interface FaxConfig {
  from_number: string | null;
  from_number_display: string | null;
  configured: boolean;
}

export interface LeadLink {
  id: string;
  lead_id: string;
  study_slug: string | null;
  lead_source?: string | null;
  eligibility_status: string | null;
  first_seen_at: string;
  lead_snapshot?: any;
}

export interface RawAnswer {
  question_id: string;
  question_type: string | null;
  value_text: string | null;
  value_num: number | null;
  value_bool: boolean | null;
  value_json: unknown;
}

export interface ScreenerAnswer {
  question_id: string;
  label: string;
  type: string | null;
  value: unknown;
  display: string;
  /** 'exclude' (disqualifying → red), 'review' (flagged → yellow), or null. */
  eligibility_kind?: 'exclude' | 'review' | null;
}

export interface ScreenerSubmission {
  lead_id: string;
  study_slug: string | null;
  study_title: string | null;
  /** Recruiter-only internal alias overlaid live (null = none); render
   *  `study_internal_title || study_title` in recruiter views. */
  study_internal_title?: string | null;
  questionnaire_name: string | null;
  /** Landing variant key this submission was served under (null = default form). */
  landing_variant?: string | null;
  eligibility_status: string | null;
  eligibility_notes: string | null;
  incomplete: boolean;
  first_seen_at: string | null;
  answer_count: number;
  answers: ScreenerAnswer[];
}

/** One named ad-landing variant of a study (/study/<slug>?v=<key>). The
 *  study's landing_form_id stays the default/control; each variant serves its
 *  own published form + optional copy overrides while leads land in the SAME
 *  study. See docs/crm-landing-variants-plan.md. */
export interface LandingVariant {
  id: string;
  study_id: string;
  /** URL selector, immutable after create (lives in ad URLs + on recorded leads). */
  key: string;
  label: string;
  form_id: string;
  /** paused = stop serving to NEW visitors (they get the default form). */
  status: 'active' | 'paused';
  /** Weighted-random share for the same-URL A/B tier (0 = never auto-assigned). */
  weight: number;
  copy_overrides: {
    condition?: string;
    compensation_text?: string;
    short_description?: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface Screener {
  submissions: ScreenerSubmission[];
  incompleteCount: number;
}

export interface Enrollment {
  id: string;
  flow_id: string;
  flow_name?: string | null;
  flow_version: number;
  status: string;
  current_node_id: string | null;
  next_run_at: string | null;
  enrolled_at: string;
  completed_at: string | null;
  last_error: string | null;
}

/** Follow-up task (crm.tasks). Returned by GET /api/crm/tasks. */
export interface Task {
  id: string;
  network_id?: string;
  contact_id: string | null;
  assignee_user_id: string | null;
  title: string;
  description: string | null;
  due_at: string | null;
  /** open | in_progress | done | cancelled (unknowns tolerated). */
  status: string;
  /** low | normal | high | urgent (unknowns tolerated). */
  priority: string;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
  /** Slim contact summary embedded on the list endpoint (null for orphans). */
  contact?: Pick<Contact, 'id' | 'full_name' | 'first_name' | 'last_name' | 'phone_e164'> | null;
  /**
   * Set ONLY when the viewer holds this row because its owner is out of office
   * today. Ownership is NOT transferred — render a CoverageBadge whenever it is
   * present, because a covered row is otherwise indistinguishable from your own
   * and a recruiter will assume it was reassigned to them.
   */
  coverage?: CoverageRef | null;
}

/** Scheduled callback (crm.scheduled_callbacks). */
export interface ScheduledCallback {
  id: string;
  scheduled_at: string;
  duration_min: number;
  /** booked | completed | cancelled | no_show (unknowns tolerated). */
  status: string;
  /** recruiter | public_link | agent (the texting agent's scheduling assistant). */
  booked_via: string;
  note: string | null;
  assignee_user_id: string | null;
  created_by?: string | null;
  created_at?: string;
  /**
   * Set ONLY when the viewer holds this row because its owner is out of office
   * today. Ownership is NOT transferred — render a CoverageBadge whenever it is
   * present, because a covered row is otherwise indistinguishable from your own
   * and a recruiter will assume it was reassigned to them.
   */
  coverage?: CoverageRef | null;
  /**
   * Scheduler mode: an assistant or scheduler-page booking. The callback editor
   * keys the "Also make <new recruiter> the lead's owner" offer on THIS flag,
   * never on booked_via (a legacy /book booking is public_link too).
   */
  scheduler_booking?: boolean;
  /**
   * Scheduler mode: what the lead was told about this booking (its
   * confirmation text's real state); drives the 'Booked by text' labels.
   */
  text_confirmation?: { state: 'sent' | 'queued' | 'not_told' | 'none'; quiet?: boolean } | null;
  /**
   * Booked on top of another appointment on the recruiter's calendar, with the
   * override (allow_overbook / overbook_ok). The card wears an "Overbooked" chip.
   */
  overbooked?: boolean;
}

/** Callback row on the worklist (carries a slim contact). */
export interface WorklistCallback extends ScheduledCallback {
  contact_id: string;
  /** The lead's AI brief when briefs are on and it is fresh (callbacks are ranked by it too). */
  brief?: Brief | null;
  contact: Pick<Contact, 'id' | 'first_name' | 'last_name' | 'full_name' | 'phone_e164' | 'lifecycle_status' | 'owner_user_id'>;
}

/** GET /api/crm/callbacks/upcoming — slim feed for the in-app reminder host. */
export interface UpcomingCallback {
  id: string;
  contact_id: string;
  scheduled_at: string;
  /** First name only — no PHI beyond the given name. */
  first_name: string | null;
  /**
   * Set ONLY when the viewer holds this row because its owner is out of office
   * today. Ownership is NOT transferred — render a CoverageBadge whenever it is
   * present, because a covered row is otherwise indistinguishable from your own
   * and a recruiter will assume it was reassigned to them.
   */
  coverage?: CoverageRef | null;
}

export interface WorklistBucket<T> {
  count: number;
  /** Absent in counts-only mode. */
  items?: T[];
}

/** One agent Review-queue item on Today (owner workflow 2026-08-03): an
 *  escalated question or a lead awaiting an eligibility determination. Stays
 *  on the daily list until resolved — presence is computed from the live
 *  queue, so completing the work removes it. */
export interface WorklistAgentReviewItem {
  conversation_id: string;
  contact_id: string;
  contact_name: string | null;
  /** An escalation kind, or 'eligibility_review' for an awaiting-review lead. */
  kind: string;
  escalation_id: string | null;
  created_at: string;
  /** A call_request the texting agent's scheduler raised: its payload.cause (scheduler mode). */
  cause?: string;
}

/**
 * One finished AI eligibility review on Today. Scoped to the person who ASKED
 * for it (not the lead's owner) and present until the review is signed off —
 * which the server refuses while any flagged criterion is unverified, so the
 * row disappears exactly when the work is actually done.
 */
export interface WorklistReviewReadyItem {
  review_id: string;
  contact_id: string;
  contact_name: string | null;
  study_id: string;
  study_title: string | null;
  status: 'completed' | 'failed';
  overall_result: 'eligible' | 'excluded' | 'review_required' | 'incomplete' | null;
  /** Failure reason; null on a completed review. */
  error: string | null;
  /** Criteria the AI flagged that nobody has verified yet. */
  pending_count: number;
  completed_at: string;
  needs_decision: boolean;
}

/**
 * One unread @mention or reply of the viewer on Today (docs/plans/crm-report-builder-search-collab-plan.md
 * B3.4; the item shape of crmMentions.listMyMentions, B3.2). Scoped to the person MENTIONED, never to
 * the lead's owner or coverage, like WorklistReviewReadyItem.
 */
export interface WorklistMentionItem {
  /** crm.note_mentions.id: what POST /api/crm/mentions/read takes. */
  id: string;
  /** 'mention' = named with "@" in a note; 'reply' = a reply under a note the viewer wrote. */
  kind: 'mention' | 'reply';
  created_at: string;
  /** null while unread (every item on Today is unread). */
  read_at: string | null;
  /** The note: `snippet` is the first 200 characters of a live note, '' for a deleted one. */
  note: {
    id: string;
    contact_id: string;
    snippet: string;
    deleted: boolean;
    occurred_at: string;
  };
  contact: { id: string; full_name: string | null };
  /** Who wrote the note; null when unknown. */
  author_user_id: string | null;
}

/**
 * The Mentions bucket of GET /api/crm/worklist (plan B3.4). `newest` is the newest unread mention,
 * sent in counts-only mode too, so the overlay card can tell a new mention from one it already
 * showed without a poll of its own.
 */
export interface WorklistMentionsBucket extends WorklistBucket<WorklistMentionItem> {
  newest?: { id: string; created_at: string } | null;
}

/**
 * The attention windows the server used (backend services/crmAttention.js; tenant settings): a lead
 * is a HOT new lead for `hot_lead_hours` after it came in or re-submitted a form, and a conversation
 * is live for `conversation_days` after the lead last wrote to us.
 */
export interface AttentionWindows {
  hot_lead_hours: number;
  conversation_days: number;
}

/**
 * GET /api/crm/worklist — the recruiter's bucketed daily to-do list, in the two ATTENTION TIERS
 * (owner, 2026-09-28): `total_actionable` (the Today badge) counts only the NEEDS-ACTION-NOW
 * buckets (callbacks, needs_reply, new_leads, conversations, agent_review, reviews_ready, mentions,
 * tasks); `keep_trying` counts the rest (not_contacted, follow_ups, stale).
 */
export interface WorklistResponse {
  generated_at: string;
  /** AI briefs switch (crm-brief plan B3.7). Show nothing brief-related unless `enabled` is true. Absent on older servers. */
  brief?: { enabled: boolean };
  max_attempts: number;
  stale_days: number;
  tasks_due: number;
  /** Safety-net lane: network-wide leads left behind (NOT part of
   *  total_actionable — it is not this recruiter's to-do count). `count` is the
   *  leads with a needs-action-now catch (the nav badge); `keep_trying` the
   *  leads whose every catch is keep-trying work. Absent on older servers —
   *  guard reads. */
  safety_net?: { count: number; keep_trying?: number };
  /** The windows behind "hot" and "live". Absent on older servers. */
  attention?: AttentionWindows;
  total_actionable: number;
  /** Due, but nobody is waiting on us (never in the badge). Absent on older servers. */
  keep_trying?: { count: number };
  buckets: {
    callbacks: WorklistBucket<WorklistCallback>;
    needs_reply: WorklistBucket<ContactListItem>;
    /** HOT new leads only: came in (or re-submitted) within the window, not heard from since. */
    new_leads: WorklistBucket<ContactListItem>;
    /** Live conversations gone quiet: they wrote within the window, the next nudge is due. Absent on older servers. */
    conversations?: WorklistBucket<ContactListItem>;
    /** Keep trying: never contacted, and not hot. Absent on older servers. */
    not_contacted?: WorklistBucket<ContactListItem>;
    /** Keep trying: the next try is due for someone who has not answered us. */
    follow_ups: WorklistBucket<ContactListItem>;
    stale: WorklistBucket<ContactListItem>;
    /** Absent on older servers — guard reads. */
    agent_review?: WorklistBucket<WorklistAgentReviewItem>;
    /** Absent on older servers — guard reads. */
    reviews_ready?: WorklistBucket<WorklistReviewReadyItem>;
    /**
     * The viewer's unread @mentions and replies (plan B3.4): scoped to the REQUESTER like
     * reviews_ready and counted in total_actionable. Absent on older servers: guard reads.
     */
    mentions?: WorklistMentionsBucket;
  };
}

/**
 * GET /api/crm/follow-ups — Today's KEEP TRYING list (the tier-2 work; backend
 * crmWorklist.getFollowUpsDue): never-contacted leads that are not hot (first),
 * then cadence follow-ups due by end of the viewer's local day (incl. overdue)
 * to people who have not answered us. Live conversations and hot new leads are
 * left out: Today's needs-action sections carry them. `scope` echoes the
 * requested mine|everyone; `truncated` is set when more rows matched than the
 * server's cap returned (narrow with filters to see the rest).
 */
export interface FollowUpsDueResponse {
  generated_at: string;
  max_attempts: number;
  scope: 'mine' | 'everyone';
  count: number;
  /** How many of `count` were never contacted. Absent on older servers. */
  not_contacted_count?: number;
  truncated: boolean;
  /** The windows behind "hot" and "live". Absent on older servers. */
  attention?: AttentionWindows;
  items: ContactListItem[];
}

// ---------------------------------------------------------------------------
// My Calendar (aggregate of callbacks + tasks + personal events)
// ---------------------------------------------------------------------------

export type CalendarSourceKind = 'callback' | 'task' | 'event';

/** What an item can be: one of the three toggleable kinds, or `busy`, a
 *  teammate's booking-blocking event with no details (GET /calendar
 *  ?include_busy=true, asked for only by the Team day view). */
export type CalendarItemKind = CalendarSourceKind | 'busy';

/**
 * One normalized item on the recruiter calendar. `editable` is true only for
 * personal events (callbacks/tasks are managed from the contact / worklist).
 * `start`/`end` are ISO; callbacks carry the patient name in `title` (in-app,
 * behind auth — the de-identification rule applies only to the Google mirror).
 */
export interface CalendarItem {
  source_kind: CalendarItemKind;
  source_id: string;
  /** Recruiter this item belongs to (callback/task assignee, or the viewer for
   *  personal events). null = unassigned callback. Drives per-person coloring
   *  when several recruiters' calendars are overlaid. */
  owner_user_id?: string | null;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  status: string | null;
  editable: boolean;
  contact_id?: string | null;
  contact_name?: string | null;
  booked_via?: string | null;
  note?: string | null;
  priority?: string | null;
  location?: string | null;
  description?: string | null;
  color?: string | null;
  /** Recurrence rule for personal events: null = single, 'weekly' = repeats. */
  recurrence?: 'weekly' | null;
  /** Weekdays a weekly series repeats on (0=Sun … 6=Sat). */
  recurrence_days?: number[] | null;
  /** Inclusive end date (YYYY-MM-DD) of a recurring series; null = open-ended. */
  recurrence_until?: string | null;
  /** Personal events only: whether this block makes the recruiter unavailable
   *  (blocks lead bookings + new-lead routing). Defaults true. */
  blocks_booking?: boolean;
  /** Unique per rendered instance (set when a recurring base is expanded into
   *  occurrences, so React keys stay unique). Falls back to source_id. */
  instance_key?: string;
  /** Callbacks only, once the slot has passed: what happened on the call
   *  (crm.callback_outcomes; worked out by the crm-worker sweep or set by hand).
   *  null = not worked out yet. */
  outcome?: CallbackOutcome | null;
}

/** The outcome keys of backend services/crmCallbackOutcome.js OUTCOMES (KEEP IN STEP). */
export type CallbackOutcomeKey =
  | 'asked_to_stop' | 'scheduled' | 'not_interested' | 'not_eligible' | 'wrong_number'
  | 'icf_sent' | 'callback_booked' | 'interested' | 'wants_call_back' | 'talked'
  | 'voicemail' | 'no_answer' | 'cancelled' | 'not_called';

export interface CallbackOutcome {
  key: CallbackOutcomeKey;
  label: string;
  /** auto = from the records, ai = the AI read the call, manual = a person chose it. */
  source: 'auto' | 'ai' | 'manual';
  /** Every outcome the evidence supports, the headline first. */
  signals: { key: CallbackOutcomeKey; label: string }[];
  /** The AI's one line about the call, or a person's note. */
  summary: string | null;
  set_by: string | null;
  decided_at: string | null;
  next_callback_at: string | null;
  calls: { tries: number; connected: number } | null;
  /** The status the sweep closed the callback to, when it did. */
  auto_closed: 'completed' | 'no_show' | null;
}

/** GET /api/crm/calendar response. */
export interface CalendarResponse {
  events: CalendarItem[];
  range: { from: string; to: string };
}

/** crm.user_calendar_events row — a personal event the caller owns. */
export interface UserCalendarEvent {
  id: string;
  network_id: string;
  user_id: string;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  location: string | null;
  color: string | null;
  recurrence: 'weekly' | null;
  recurrence_days: number[] | null;
  recurrence_until: string | null;
  blocks_booking: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Why someone is away. `out` is the unlabelled default. */
export type AbsenceKind = 'out' | 'pto' | 'sick' | 'training' | 'other';

/**
 * crm.user_absences row — one contiguous out-of-office stretch.
 *
 * Deliberately TEAM-VISIBLE, unlike UserCalendarEvent (which is owner-private):
 * the whole point is that colleagues can see who is away and cover for them.
 * `starts_on`/`ends_on` are inclusive YYYY-MM-DD calendar days in the tenant
 * timezone — never instants, so there is no boundary hour to get wrong.
 * `created_by !== user_id` means somebody marked somebody else out.
 */
export interface UserAbsence {
  id: string;
  user_id: string;
  /** Hydrated by the API for display; absent on write payloads. */
  user_name?: string | null;
  starts_on: string;
  ends_on: string;
  kind: AbsenceKind;
  note: string | null;
  created_by: string | null;
  created_by_name?: string | null;
  created_at?: string;
  /** Set when the absence was filed from the calendar's PTO control. */
  calendar_event_id?: string | null;
}

/** GET /api/crm/absences response. */
export interface AbsencesResponse {
  absences: UserAbsence[];
  range: { from: string; to: string };
  /** User ids out on the tenant's current day — the cheap "is X out now?" set. */
  out_today: string[];
}

/**
 * Attached by the API to any work row a viewer holds only because its real
 * owner is out today. Never present on a viewer's own work, so its presence
 * alone is the "this is coverage" signal — render a badge whenever it is set.
 */
export interface CoverageRef {
  /** The absent owner whose work this is. Ownership is NOT transferred. */
  owner_user_id: string;
  owner_name: string | null;
  /** Inclusive last day of their absence (YYYY-MM-DD) — powers "back Monday". */
  ends_on: string | null;
}

export interface Communication {
  id: string;
  channel: string;
  direction: string | null;
  status: string | null;
  body: string | null;
  user_id: string | null;
  external_provider: string | null;
  occurred_at: string;
  metadata: any;
  /**
   * Notes you can correct and replies (docs/plans/crm-report-builder-search-collab-plan.md B1.1
   * and B3.1). Optional: an older api leaves them out. created_at is when the row was written;
   * the 24-hour edit window is measured from it (crm/notes/noteLogic.ts noteActions). ONLY
   * edited_at and edit_count mean "edited": updated_at also moves on system refreshes and merges.
   * A deleted note keeps its row with body '' and deleted_at / deleted_by set (render the marker,
   * never the text). reply_to_id is the top-level note this note answers.
   */
  created_at?: string | null;
  edited_at?: string | null;
  edited_by?: string | null;
  edit_count?: number;
  deleted_at?: string | null;
  deleted_by?: string | null;
  reply_to_id?: string | null;
}

/** The contact's pinned primary study, resolved on the detail response. */
export interface PrimaryStudyRef {
  id: string;
  slug: string;
  title: string;
  /** Recruiter-only internal alias (null = none); render via studyInternalLabel. */
  internal_title?: string | null;
}

/**
 * Per-study eligibility verdict (#08), rolled up from study_attempts on the
 * detail response. `attempt_status` is the raw journey status (badge via
 * attemptStatusMeta); `eligibility` is the compact verdict the inbox filter
 * shares (eligible | screening | not_eligible | withdrew).
 */
export interface StudyEligibilityRow {
  study_id: string;
  study_slug: string | null;
  study_title: string | null;
  attempt_status: string;
  eligibility: string;
  started_at: string | null;
  outcome_at: string | null;
}

export interface ContactDetailResponse {
  contact: Contact;
  leads: LeadLink[];
  answers: RawAnswer[];
  screener: Screener;
  /** Resolved label for contact.primary_study_id (null when none pinned). */
  primary_study?: PrimaryStudyRef | null;
  /** Per-study eligibility chips (#08); empty when no screening journey yet. */
  study_eligibility?: StudyEligibilityRow[] | null;
  /**
   * Study-list memberships — every study this lead has ever been considered
   * for, with an independent status on each (multi-study lead lists). Drives
   * the Studies card; empty until the lead lands on a list.
   */
  memberships?: ContactStudyMembership[] | null;
  /** Tenant cadence ceiling for "attempt N of M" copy (defaults to 6). */
  max_attempts?: number;
  /** Phase G: six-probe completeness meter (null when the probe failed). */
  profile_completeness?: ProfileCompleteness | null;
  /** Phase G: header ratings chip (null when the probe failed). */
  rating_summary?: RatingSummary | null;
}

// ---------------------------------------------------------------------------
// Medical profile + interaction ratings (Expansion Phase G)
// ---------------------------------------------------------------------------

/** A contact medication or condition row (crm.contact_medications/_conditions). */
export interface ProfileItem {
  id: string;
  contact_id: string;
  name: string;
  name_normalized: string;
  /** Medications only. */
  dose?: string | null;
  frequency?: string | null;
  /** meds: active | stopped | unknown; conditions: active | resolved | unknown. */
  status: string;
  /** form | recruiter | ai_extraction | lead. */
  source: string;
  source_ref?: string | null;
  profile_key?: string | null;
  /** Provenance for AI-extracted facts (the call/text/fax it came from). */
  source_communication_id?: string | null;
  source_document_id?: string | null;
  /** Provenance for form-derived facts (the completed form response). */
  source_form_response_id?: string | null;
  source_excerpt?: string | null;
  /** AI extraction confidence: high | medium | low (null for recruiter-entered). */
  confidence?: string | null;
  verified_by: string | null;
  verified_at: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
}

/** A contact lab/test result row (crm.contact_lab_results). */
export interface LabResult {
  id: string;
  contact_id: string;
  test_name: string;
  test_name_normalized: string;
  value_num?: number | null;
  value_text?: string | null;
  unit?: string | null;
  reference_low?: number | null;
  reference_high?: number | null;
  /** low | high | normal | abnormal | unknown. */
  abnormal_flag?: string | null;
  /** e.g. fasting | random | postprandial. */
  specimen_context?: string | null;
  result_date?: string | null;
  date_precision?: string | null;
  /** form | recruiter | ai_extraction | lead. */
  source: string;
  source_ref?: string | null;
  source_communication_id?: string | null;
  source_document_id?: string | null;
  source_form_response_id?: string | null;
  source_excerpt?: string | null;
  confidence?: string | null;
  profile_key?: string | null;
  verified_by: string | null;
  verified_at: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
}

/** Append-only interaction rating (crm.interaction_ratings). */
export interface InteractionRating {
  id: string;
  contact_id: string;
  recruiter_user_id: string | null;
  rating: number;
  note: string | null;
  communication_id: string | null;
  created_at: string;
}

export interface RatingSummary {
  /** Mean to 1 decimal; null until the first rating. */
  average: number | null;
  count: number;
}

export interface ProfileCompleteness {
  percent: number;
  /** Stable keys: phone | email | medications | conditions | completed_form | answers. */
  missing: string[];
}

/** Best display name for a contact (full_name, else first+last, else placeholder). */
export function contactName(c: Pick<Contact, 'full_name' | 'first_name' | 'last_name'>): string {
  return c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '(no name)';
}

/** A study-shaped object carrying the recruiter (internal) and/or public title. */
export interface StudyLabelParts {
  title?: string | null;
  internal_title?: string | null;
  slug?: string | null;
}

/**
 * Recruiter-facing study label: the admin-only internal title when set, else the
 * public title (then slug). Use everywhere INSIDE the CRM where a recruiter/admin
 * reads a study name. NEVER use for patient-facing copy.
 */
export function studyInternalLabel(s: StudyLabelParts): string {
  return s.internal_title || s.title || s.slug || '';
}

/**
 * Public-facing study label: the public `title` (then slug). Use for what a
 * patient sees — outbound-message previews, the public website. Never the
 * internal title.
 */
export function studyPublicLabel(s: StudyLabelParts): string {
  return s.title || s.slug || '';
}

// ---------------------------------------------------------------------------
// Forms engine (Expansion Phase C)
// ---------------------------------------------------------------------------

/**
 * One cohort answer criterion (the GET /contacts `answers` param — see
 * backend services/crmAnswerQuery.js). Criteria AND together and match the
 * unified answer view, so a hit counts wherever the patient gave the answer
 * (web lead, recruiter prescreen, or texted form).
 */
export interface AnswerCriterion {
  /** question_key: WCR UUID, 'ps.*' prescreen key, or builder 'form.*' key. */
  question: string;
  /** eq | neq | gt | gte | lt | lte | contains | includes | not_includes | answered. */
  op: string;
  /** Comparison value as a string ('' for op 'answered'). */
  value: string;
  /** question_type from the form definition — routes typed-column matching. */
  type?: string;
  /** Display-only: the question's label for chips (never sent to the API). */
  label?: string;
  /** Display-only: the option's label when `value` is a choice option value. */
  valueLabel?: string;
}

/** One branching condition: hide the question until the referenced answer matches. */
export interface FormShowIfCondition {
  question_key: string;
  /** equals | not_equals | contains | greater_than | less_than | in | not_in. */
  operator: string;
  /** `in` / `not_in` carry a LIST (exact membership); every other operator a scalar. */
  value: string | number | boolean | (string | number | boolean)[];
}

/** Two or more conditions combined with any (OR) / all (AND). */
export interface FormShowIfGroup {
  match: 'any' | 'all';
  conditions: FormShowIfCondition[];
}

/**
 * Branching rule. A SINGLE condition is stored flat (the legacy shape every
 * pre-existing form carries, and what the WCR site has always accepted); two or
 * more are stored as a { match, conditions } group. crmForms.validateDefinition
 * normalizes back down to the flat shape whenever only one condition survives.
 */
export type FormShowIf = FormShowIfCondition | FormShowIfGroup;

export const isShowIfGroup = (s: FormShowIf | null | undefined): s is FormShowIfGroup =>
  !!s && Array.isArray((s as FormShowIfGroup).conditions);

/** Either shape → a uniform { match, conditions }; null when nothing is set. */
export function normalizeShowIf(showIf: FormShowIf | null | undefined): FormShowIfGroup | null {
  if (!showIf) return null;
  if (isShowIfGroup(showIf)) {
    const conditions = showIf.conditions.filter((c) => c && typeof c.question_key === 'string');
    if (conditions.length === 0) return null;
    return { match: showIf.match === 'all' ? 'all' : 'any', conditions };
  }
  if (typeof showIf.question_key !== 'string') return null;
  // Single condition: 'any' and 'all' agree.
  return { match: 'all', conditions: [showIf] };
}

/** Collapse back to what the API stores: one condition flat, several grouped. */
export function denormalizeShowIf(group: FormShowIfGroup | null): FormShowIf | null {
  if (!group || group.conditions.length === 0) return null;
  if (group.conditions.length === 1) return group.conditions[0];
  return group;
}

/** WCR-compatible qualification rule on one question. */
export interface FormEligibilityRule {
  exclude_if?: { operator: string; value: any } | null;
  flag_for_review_if?: { operator: string; value: any } | null;
}

/**
 * Optional tag: an affirmative/recorded answer to this question is written as a
 * structured medical fact on the contact's profile (so cohort queries like
 * "type 2 diabetes" / "BMI > 30" capture the lead). Mirrors crmForms.MEDICAL_FACT_KINDS.
 */
export interface FormMedicalFact {
  /** condition | medication | lab — the destination profile table. */
  kind: 'condition' | 'medication' | 'lab';
  /** The canonical fact recorded (e.g. "Type 2 diabetes"). */
  name?: string;
  /** Precise cross-study key (e.g. condition.t2dm) for exact querying. */
  profile_key?: string;
  /** Override the default "is this affirmative?" test (default: yes = Yes, numeric = a value). */
  positive_when?: { operator: string; value: any };
}

/** One question in a form definition (crm.forms.definition / form_versions). */
export interface FormQuestionDef {
  /** Immutable. Builder-minted `form.*` slug or an imported WCR question id. */
  key: string;
  /** text|textarea|email|phone|number|height|date|single_choice|multiple_choice|yes_no|scale|calculation. */
  type: string;
  label: string;
  required?: boolean;
  description?: string | null;
  placeholder?: string | null;
  options?: { value: string; label: string }[] | null;
  min?: number | null;
  max?: number | null;
  show_if?: FormShowIf | null;
  eligibility?: FormEligibilityRule | null;
  calculation?: { preset: string; inputs: Record<string, string> } | null;
  medical_fact?: FormMedicalFact | null;
}

export interface FormDefinition {
  questions: FormQuestionDef[];
  /** Outcome messages, greeting, Phase I passthrough — bounded scalars/arrays. */
  settings: Record<string, any>;
}

/** A form row (list + detail). */
export interface CrmForm {
  id: string;
  study_id: string | null;
  /** comprehensive | lead_gen | custom (legacy secondary tag; `surface` is primary). */
  kind: string;
  /** Item #05 group: 'landing' (WCR ad page) | 'questionnaire' (texted /f link). */
  surface?: string;
  title: string;
  description: string | null;
  therapeutic_area: string | null;
  /** draft | active | archived. */
  status: string;
  /** Current published version (0 = never published). */
  version: number;
  definition?: FormDefinition;
  imported_questionnaire_id?: string | null;
  import_meta?: any;
  created_at: string;
  updated_at: string;
  counts?: { responses: number; completed: number };
  /** GET /forms: the linked study's public title (Search's form picker names it). Older apis omit it. */
  study_title?: string | null;
  /** GET /forms: landing leads submitted on this form (contact_leads whose questionnaire_id is this
   *  form or its imported questionnaire) — a landing page has no form_responses, so `counts` reads 0
   *  for it. Older apis omit it. */
  landing_leads?: number;
}

export interface FormVersionSummary {
  id: string;
  version: number;
  published_by: string | null;
  published_at: string;
}

/** Latest AI generation targeting a form (embedded on GET /forms/:id — the
 *  builder polls it while 'generating' and locks editing). */
export interface FormAiGeneration {
  artifact_id: string;
  /** generating | published (landed) | failed. */
  status: string;
  error: string | null;
  /** 'revise' (rewrites the current definition) | 'fresh' (from the protocol). */
  mode: 'revise' | 'fresh';
  updated_at: string;
}

// ---- Automation flows (Part 6 builder) -------------------------------------

/** Wait/timeout duration: a number of SECONDS or day/hour parts. */
export type FlowDuration = number | { days?: number; hours?: number; minutes?: number; seconds?: number };

export interface FlowConditionDef {
  when: { field?: string; op?: string; value?: any };
  goto?: string | null;
}

/** One node of a flow definition — plan §6.11 is the authoritative schema. */
export interface FlowNodeDef {
  type: string;
  /** Edge to the next node; null/omitted completes the enrollment. */
  next?: string | null;
  // send_sms / send_email
  body?: string;
  subject?: string;
  quiet_hours_exempt?: boolean;
  // wait
  duration?: FlowDuration;
  until_business_hours?: boolean;
  timezone?: string;
  // wait_for_reply
  timeout?: FlowDuration;
  on_timeout?: string | null;
  // branch
  conditions?: FlowConditionDef[];
  else?: string | null;
  // create_task
  title?: string;
  description?: string | null;
  priority?: string;
  // update_contact
  set?: Record<string, any>;
}

export interface FlowDefinition {
  /** Explicit entry node id — the builder always writes it (§6.11). */
  entry: string | null;
  nodes: Record<string, FlowNodeDef>;
}

/** An automation_flows row (list + builder). */
export interface CrmFlow {
  id: string;
  name: string;
  description: string | null;
  /** draft | active | archived. */
  status: string;
  /** Current published version (0 = never published). */
  version: number;
  trigger_type: string;
  trigger_config: Record<string, string | string[]>;
  enrollment_rules: { reenroll?: boolean; dedupe_window_hours?: number };
  definition?: FlowDefinition;
  created_at: string;
  updated_at: string;
  counts?: { active_enrollments: number };
}

export interface FlowVersionSummary {
  id: string;
  version: number;
  published_by: string | null;
  published_at: string;
}

/** A contact's texted form link (GET /contacts/:id/form-links). */
export interface ContactFormLink {
  id: string;
  form_id: string;
  form_title: string;
  form_kind: string | null;
  /** created | sent | started | completed | expired | cancelled (display-derived). */
  status: string;
  expires_at: string;
  sent_at: string | null;
  created_at: string;
  url: string;
  response: {
    id: string;
    status: string;
    eligibility_status: string | null;
    completed_at: string | null;
  } | null;
}

/** Label-resolved response detail (GET /form-responses/:id). */
export interface FormResponseDetail {
  id: string;
  contact_id: string;
  form_id: string;
  form_title: string;
  version: number | null;
  status: string;
  eligibility_status: string | null;
  /** The STORED verdict — null until the engine scored the response (an
   *  in-progress record, or an agent record completed from a partial answer
   *  set). Highlight from `answers[].eligibility_kind`, which the server derives
   *  when this is absent; this stays the audit record. */
  eligibility_reasons: { question_key: string; kind: string; label: string }[] | null;
  started_at: string;
  completed_at: string | null;
  /** §6.6c: 'agent' = the texting agent filled this from the SMS conversation;
   *  'lead' (or absent on older servers) = the lead's own submission. */
  source?: 'lead' | 'agent';
  answers: {
    question_key: string;
    label: string;
    type: string | null;
    display: string;
    /** Red (met an exclusion rule) / yellow (met a needs-review rule). Absent on
     *  older servers — callers fall back to `eligibility_reasons`. */
    eligibility_kind?: 'exclude' | 'review' | null;
  }[];
}

// ---------------------------------------------------------------------------
// AI eligibility review (CRM medical history review)
// ---------------------------------------------------------------------------

/**
 * Pipeline status of one review (crm.eligibility_reviews.status).
 * 'extracting_facts' / 'evaluating' are facts-engine-only in-flight states
 * (legacy reviews never pass through them; additive to the CHECK constraint).
 */
export type EligibilityReviewStatus =
  | 'queued'
  | 'building_dossier'
  | 'analyzing'
  | 'verifying'
  | 'extracting_facts'
  | 'evaluating'
  | 'completed'
  | 'failed'
  | 'cancelled';

/** Overall AI verdict for one contact × study review. */
export type EligibilityOverallResult = 'eligible' | 'excluded' | 'review_required' | 'incomplete';

/**
 * Per-criterion verdict (MHR's RESULT_TYPES + the CRM-only 'not_applicable' —
 * a criterion that structurally cannot apply to this patient, e.g. a
 * female-only requirement for a male lead; always eligible-impact).
 */
export type EligibilityCriterionResultValue = 'met' | 'not_met' | 'uncertain' | 'not_found' | 'contradictory' | 'not_applicable';

/**
 * Segment-coverage stats stored on counts.coverage — how much of the dossier
 * the criterion citations touched, and whether the uncited-segment
 * verification pass ran (it only runs when >5% of the corpus went uncited).
 */
export interface EligibilityCoverage {
  segments_total?: number;
  segments_cited?: number;
  total_chars?: number;
  uncited_chars?: number;
  verification_ran?: boolean;
}

/**
 * Provenance of one dossier segment (legacy) or fact/citation (facts engine)
 * — where a piece of the patient's story came from. `document_page` sources
 * resolve to a real medical-record page (the segment panel / PDF jump offers
 * "Open document"); the rest point at comms/forms/profile/demographics. `lead`
 * is facts-engine-only (a structured answer recorded at intake).
 */
export interface EligibilitySourceRef {
  type: 'document_page' | 'communication' | 'form_response' | 'attempt' | 'profile' | 'demographics' | 'lead';
  document_id?: string;
  page?: number;
  communication_id?: string;
  lead_id?: string;
  form_response_id?: string;
  attempt_id?: string;
  occurred_at?: string;
  label: string;
}

/**
 * One numbered segment of the contact dossier
 * (GET /api/crm/eligibility-reviews/:id/segments/:n → { segment }).
 */
export interface DossierSegment {
  n: number;
  label: string;
  source: EligibilitySourceRef;
  text: string;
}

/**
 * One citation on a criterion result — points at a dossier segment (legacy
 * engine) OR, for the facts engine, has no segment at all: `segment_number`
 * is null and the citation is rendered directly from `excerpt` + `source`
 * (no `/segments/:n` fetch). `fact_id` is present when the citation came
 * from a `contact_facts` row (facts engine only).
 */
export interface EligibilityCitation {
  segment_number: number | null;
  /** ≤500-char excerpt of the evidence text. */
  excerpt: string;
  evidence_type: 'supports' | 'contradicts' | 'mentions';
  source: EligibilitySourceRef;
  /** Facts engine only — the contact_facts row this citation resolved from. */
  fact_id?: string;
}

/**
 * Per-criterion AI evaluation (crm.eligibility_criterion_results), joined with
 * the criterion's text/kind/number/category on the review detail endpoint.
 */
export interface EligibilityCriterionResult {
  id: string;
  review_id: string;
  criterion_id: string;
  result: EligibilityCriterionResultValue;
  confidence_score: number | null;
  confidence_level: 'high' | 'medium' | 'low' | 'unable' | null;
  confidence_factors: any;
  ai_reasoning: string | null;
  has_contradiction: boolean;
  contradiction_severity: string | null;
  from_verification_pass: boolean;
  requires_human_review: boolean;
  citations: EligibilityCitation[];
  human_verified: boolean;
  human_result: EligibilityCriterionResultValue | null;
  human_override_reason: string | null;
  human_notes: string | null;
  verified_by: string | null;
  verified_at: string | null;
  created_at: string;
  // Joined from crm.study_review_criteria on the detail endpoint.
  criterion_kind?: 'inclusion' | 'exclusion';
  criterion_number?: string;
  category?: string;
  criterion_text?: string;
  plain_language?: string | null;
  is_critical?: boolean;
  // ---- Facts engine (Phase 3) — how this verdict was reached ----
  /** 'deterministic' (Tier 0, code-only) | 'llm_small' (Tier 1, Haiku) |
   *  'llm_large' (Tier 2, Sonnet) | 'legacy' | null (legacy rows / not set). */
  resolution_tier?: 'deterministic' | 'llm_small' | 'llm_large' | 'legacy' | null;
  /** Facts-engine retrieval transparency: {terms, fact_hits, snippet_hits, pages, …}. */
  retrieval_meta?: {
    terms?: string[];
    fact_hits?: number;
    snippet_hits?: number;
    answer_hits?: number;
    pages?: number[];
    escalations?: string[];
    [key: string]: unknown;
  } | null;
}

/**
 * One AI eligibility review of a contact against one study
 * (crm.eligibility_reviews row; list rows add study_title +
 * criteria_set_version).
 */
export interface EligibilityReview {
  id: string;
  network_id?: string;
  contact_id: string;
  study_id: string;
  /** Groups the reviews started by one button click. */
  batch_id: string;
  dossier_id: string | null;
  criteria_set_id: string;
  status: EligibilityReviewStatus;
  overall_result: EligibilityOverallResult | null;
  summary: string | null;
  /** {met, not_met, uncertain, not_found, contradictory, not_applicable, exclusions_met, coverage?, extraction? (facts engine)}. */
  counts: {
    met?: number;
    not_met?: number;
    uncertain?: number;
    not_found?: number;
    contradictory?: number;
    not_applicable?: number;
    exclusions_met?: number;
    coverage?: EligibilityCoverage;
    /** Facts engine only — per-document fact-extraction outcomes for this
     *  review's contact (there is no dossier to report coverage on instead). */
    extraction?: {
      documents_total?: number;
      documents_ok?: number;
      documents_failed?: { document_id: string; reason?: string; filename?: string }[];
      facts_used?: number;
    };
    /** Facts engine only — echoes review.engine for callers reading counts alone. */
    engine?: 'legacy' | 'facts';
    [key: string]: unknown;
  } | null;
  criteria_total: number | null;
  criteria_evaluated: number | null;
  estimated_cost: number | null;
  actual_cost: number | null;
  input_tokens: number;
  output_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  model_id: string | null;
  error: string | null;
  requested_by: string | null;
  signed_off_by: string | null;
  signed_off_at: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  /** Recruiter-facing study label (internal-title fallback), on the list rows. */
  study_title?: string | null;
  criteria_set_version?: number | null;
  /** List rows: true when any criterion result carries a human override. */
  has_overrides?: boolean;
  /** Prompt/model provenance recorded when the review ran (null for older runs). */
  prompt_meta?: EligibilityPromptRunMeta | null;
  /** Which evaluation engine ran this review — 'legacy' (default; whole-dossier
   *  Sonnet analysis) or 'facts' (compiled rules + tiered retrieval). Null on
   *  older rows predating the column — treat as legacy. */
  engine?: 'legacy' | 'facts' | null;
  /** Recruiter-chosen evidence scope this review ran against (source-scoping
   *  addendum). NULL/absent = legacy "all sources". Identity + recruiter-entered
   *  profile facts are always included regardless. */
  source_selection?: {
    medical_records?: { enabled?: boolean; document_ids?: string[] };
    communications?: { enabled?: boolean };
    forms?: { enabled?: boolean };
  } | null;
}

/** A dossier source that could not be included in the AI's reading. */
export interface EligibilityExcludedSource {
  type: string;
  id?: string;
  reason?: string;
  detail?: string;
}

/** GET /api/crm/eligibility-reviews/:id — the full results-page payload. */
export interface EligibilityReviewDetailResponse {
  review: EligibilityReview;
  results: EligibilityCriterionResult[];
  dossier: {
    segment_count: number | null;
    excluded_sources: EligibilityExcludedSource[];
    built_at: string | null;
  } | null;
  /** Source documents for the results-page viewer (faxed + uploaded records). */
  documents?: EligibilityReviewDocument[];
  /** §3.5 staleness: the criteria set derives from eReg document versions that
   *  have since been superseded — the queue shows an informational line. */
  criteria_source_stale?: boolean;
}

/**
 * One source document available in the results-page split-view source viewer
 * (the detail response's `documents` array). Bytes come from the existing
 * GET /api/crm/documents/:id/file proxy, never a presigned URL.
 */
export interface EligibilityReviewDocument {
  id: string;
  filename: string | null;
  page_count: number | null;
  source: string | null;
  created_at: string | null;
}

/** Which version of one prompt a review ran with (review.prompt_meta). */
export interface EligibilityPromptVersionRef {
  version: number | null;
  source: string | null;
}

/**
 * Prompt/model provenance recorded at run time — which prompt versions and
 * model actually produced this review's results. Facts-engine rows key by
 * `tier1_judge`/`tier2_judge`; rows from the retired legacy engine carry
 * `review_system`/`criterion_analysis` instead (verbatim, era-specific keys —
 * never both). Typed loosely (all keys optional) so ONE type covers both eras;
 * always check for the era's actual keys before reading a version ref.
 */
export interface EligibilityPromptRunMeta {
  /** Facts engine (current). */
  tier1_judge?: EligibilityPromptVersionRef;
  tier2_judge?: EligibilityPromptVersionRef;
  /** Legacy engine (retired 2026-07-09) — historical rows only. */
  review_system?: EligibilityPromptVersionRef;
  criterion_analysis?: EligibilityPromptVersionRef;
  model_id?: string | null;
  recorded_at?: string | null;
}

/** One prompt as currently configured in the admin-editable registry. */
export interface EligibilityPromptCurrent {
  key: string;
  text: string;
  version: number | null;
  source: string | null;
  max_tokens?: number | null;
}

/**
 * GET /api/crm/eligibility-reviews/:id/prompts — current vs ran prompt info.
 * `current` is always the two live facts-engine judge prompts, regardless of
 * which engine the review itself ran on (there is no "current legacy prompt"
 * anymore). `ran` is a VERBATIM passthrough of the review's stored
 * `prompt_meta` — null when the run predates prompt-version recording, and,
 * for legacy-era reviews, keyed by `review_system`/`criterion_analysis`
 * rather than `tier1_judge`/`tier2_judge` (see EligibilityPromptRunMeta).
 */
export interface EligibilityReviewPromptsResponse {
  review: EligibilityReview;
  /** Which engine ran this review ('facts' | 'legacy' | null for older rows). */
  engine: EligibilityReview['engine'];
  current: {
    tier1_judge: EligibilityPromptCurrent;
    tier2_judge: EligibilityPromptCurrent;
  };
  ran: EligibilityPromptRunMeta | null;
  /** Route where admins edit these prompts (e.g. '/crm/prompts'). */
  editable_in: string;
}

// ---------------------------------------------------------------------------
// Search tab — cohort builder (docs/crm-search-builder-plan.md §7.7 / §14)
// ---------------------------------------------------------------------------

/**
 * One criterion instance. Loosely typed on purpose: the server-side catalog
 * (backend services/crmSearch/catalog.js) is the validation authority; the
 * builder only needs the fields each kind's editor reads/writes.
 */
export interface SearchCondition {
  kind: string;
  // pipeline
  statuses?: string[];
  slugs?: string[];
  scope?: 'current' | 'ever';
  user_ids?: string[];
  include_unassigned?: boolean;
  values?: string[];
  keys?: string[];
  sources?: string[];
  tags?: string[];
  /** interest: future-study list keys/ids. */
  lists?: string[];
  from?: string | null;
  to?: string | null;
  field?: string;
  op?: string;
  days?: number;
  channel?: string;
  state?: string;
  /** stage_ever: spine floor (1 new … 7 randomized), XOR statuses. */
  min_ordinal?: number | string;
  /** disposition */
  to_statuses?: string[];
  reason_codes?: string[];
  study_slugs?: string[];
  // medical
  tag?: string;
  tag_label?: string;
  name?: string;
  test_name?: string;
  value?: string | number | boolean;
  value2?: number | string;
  within_days?: number | string;
  require_verified?: boolean;
  preset?: string;
  /** demographic: 'age' | 'sex' */
  key?: string;
  // eligibility
  study_id?: string;
  verdicts?: string[];
  results?: string[];
  latest_only?: boolean;
  categories?: string[];
  // engagement (communication)
  channels?: string[];
  direction?: string;
  min_count?: number | string;
  // forms
  form_id?: string;
  status?: string;
  question?: string;
  type?: string;
  source?: string;
  doc_types?: string[];
  /**
   * answer / answered display fields. Stored with the saved definition (the server keeps the posted
   * definition verbatim and its validators leave these out of the evaluated criterion), so a saved
   * search, its runs, the funnel bars and the drill-ins read the question's words, not its key.
   * Definitions saved before they were kept are healed from GET /search/question-labels.
   */
  label?: string;
  /** The picked option's words when the stored `value` is an option code. */
  valueLabel?: string;
  /** The form the question was picked from — kept even when the step isn't pinned to it, so the
   *  editor reopens on that form. */
  picker_form_id?: string;
  picker_form_title?: string;
  /** 'landing' | 'questionnaire' — picks "landing page" vs "texted form" in the sentence. */
  picker_form_surface?: string;
  /** Set server-side on runs of old definitions whose kind was retired. */
  degraded?: boolean;
}

/**
 * GET /api/crm/search/question-labels?keys=k1,k2 — what a question key means, read from every
 * form's current definition and its latest published version. `forms` lists the forms that ask it.
 */
export interface QuestionLabelInfo {
  label: string;
  type: string | null;
  options: { value: string; label: string }[];
  forms: { id: string; title: string; surface: string | null }[];
}

export interface QuestionLabelsResponse {
  labels: Record<string, QuestionLabelInfo>;
}

export interface SearchStepDef {
  id: string;
  label?: string | null;
  mode: 'include' | 'exclude';
  match: 'any' | 'all';
  /** Include mode: also pass leads with NO data for this dimension. */
  keep_unknown?: boolean;
  /** Exclude mode: also drop leads with NO data for this dimension. */
  drop_unknown?: boolean;
  conditions: SearchCondition[];
}

export interface SearchDefinition {
  version: 1;
  base: {
    exclude_archived?: boolean;
    exclude_test?: boolean;
    created_from?: string | null;
    created_to?: string | null;
  };
  steps: SearchStepDef[];
}

/** Per-step three-way accounting (matched / excluded-with-data / no-data). */
export interface SearchStepStats {
  step_id: string;
  label: string | null;
  mode: string;
  match: string;
  entering: number;
  matched: number;
  with_data: number;
  no_data: number;
  excluded: number;
  passing: number;
  coverage_pct: number;
  warnings: string[];
}

/** Hydrated board-style row in previews / member lists / drill-ins. */
export interface SearchLeadRow {
  id: string;
  full_name: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone_e164?: string | null;
  email?: string | null;
  lifecycle_status: string;
  incoming_eligibility?: string | null;
  owner_user_id?: string | null;
  effective_study_slug?: string | null;
  created_at?: string;
  last_activity_at?: string | null;
  is_new?: boolean;
  stage_at_run?: string | null;
  matched_facts?: SearchMatchedFact[];
}

/** Drill-in provenance chip: the fact that satisfied the condition. */
export interface SearchMatchedFact {
  label: string;
  detail?: string | null;
  source?: string | null;
  confidence?: string | null;
  verified?: boolean;
  excerpt?: string | null;
  date?: string | null;
}

export interface SearchPreviewStep extends SearchStepStats {
  cache_hits?: number;
  samples: { matched: SearchLeadRow[]; excluded: SearchLeadRow[]; no_data: SearchLeadRow[] };
}

export interface SearchPreviewResponse {
  budget_exceeded?: boolean;
  base_total?: number;
  final_total?: number;
  rows_fetched?: number;
  steps?: SearchPreviewStep[];
  preview_rows?: SearchLeadRow[];
}

export interface SearchRunSummary {
  id: string;
  status: string; // queued | running | complete | failed
  trigger?: string;
  search_id?: string | null;
  base_total: number | null;
  final_total: number | null;
  new_matches: number | null;
  error?: string | null;
  started_at?: string | null;
  finished_at: string | null;
  duration_ms?: number | null;
  created_at: string;
  step_stats?: SearchStepStats[] | null;
  definition?: SearchDefinition;
}

export interface SavedSearch {
  id: string;
  name: string;
  description: string | null;
  status: string; // active | archived
  schedule: { freq: string; hour: number; dow?: number; dom?: number } | null;
  schedule_enabled: boolean;
  next_run_at: string | null;
  notify: { user_ids: string[]; on: string } | null;
  last_run_id: string | null;
  definition: SearchDefinition;
  schedule_summary?: string;
  created_by?: string | null;
  updated_at: string;
  created_at: string;
  last_run?: SearchRunSummary | null;
}

/** GET /search-runs/:id/compare — vs the previous completed run. */
export interface SearchRunCompare {
  prev_run: { id: string; final_total: number | null; finished_at: string | null; created_at: string } | null;
  final_delta?: number;
  added_count?: number;
  dropped_count?: number;
  added?: SearchLeadRow[];
  dropped?: SearchLeadRow[];
}

/** GET /search-runs (recent strip): a run row + its search's name. */
export interface RecentSearchRun extends SearchRunSummary {
  search_name?: string | null;
}

// ---- Search exports (P3, plan §5.4/§6.6/§6.7/§7.5) --------------------------

/** crm.search_exports row — one attach/campaign launch against a run. */
export interface SearchExport {
  id: string;
  run_id: string;
  study_id: string | null;
  /** attach | campaign | attach_campaign. */
  kind: string;
  /** pending | running | complete | failed | canceled. */
  status: string;
  params?: any;
  counts?: any;
  error?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
}

/** Attach half of the dry-run preflight (§6.6). */
export interface ExportAttachPreflight {
  selected: number;
  to_tag: number;
  to_set_considering: number;
  to_assign_owner: number;
  warnings: string[];
  tag: string | null;
  roster_size: number;
  set_considering?: 'move_new' | 'fill_empty' | 'overwrite' | 'off';
  /** move_new only — what the new lead rows name as their source ("Search: <name>"). */
  lead_source?: string | null;
  /** move_new only: opened on this study as new leads. */
  to_move?: number;
  /** move_new only: already waiting on this study at a working stage, focused elsewhere — moved back. */
  to_move_back?: number;
  /** move_new only: of the moved, how many were closed or archived. */
  to_reopen?: number;
  /** move_new only: of the moved, how many were being considered for another study (they stay on its list). */
  from_other_study?: number;
  /** move_new only: moved people with no owner who will get one of the study's recruiters. */
  auto_owner?: number;
  /** move_new only: people left exactly as they are, by reason. */
  kept?: { committed: number; already_on_study: number; in_agent_conversation: number; on_outreach_list: number };
}

/** Campaign half of the dry-run preflight (§6.7) — live skip-rule counts. */
export interface ExportCampaignPreflight {
  selected: number;
  to_schedule: number;
  skips: Record<string, number>;
  cap: number;
  cap_exceeded: boolean;
  warn_large: boolean;
  first_send_at: string | null;
  last_send_at: string | null;
  channel: string;
  pace_per_hour: number;
  /** Who the message can be previewed as (the first few who will get it, as they'll read it after the attach). */
  preview?: {
    people: Array<{ first_name: string; last_name: string; full_name: string; study_title: string; study_slug: string }>;
    /** Of everyone who'll get it: no first name on file. */
    no_first_name: number;
    /** Of everyone who'll get it: on a different study when it sends, so {{study_title}} names that one. */
    not_on_study: number;
  };
  /** Only when the request named a template_id (the modal now sends the words themselves). */
  template_error?: string | null;
  template?: { id: string; title: string; channel: string } | null;
}

/** POST /search-runs/:id/export {dry_run:true} response. */
export interface ExportPreflight {
  selected: number;
  missing: number;
  study: { id: string; slug: string; title: string };
  attach?: ExportAttachPreflight;
  campaign?: ExportCampaignPreflight;
}

/** One receipt row from GET /search-exports/:id (hydrated). */
export interface ExportReceiptItem {
  contact_id: string;
  /** attached | owner_assigned | scheduled | skipped | failed. */
  outcome: string;
  detail: string | null;
  scheduled_message_id: string | null;
  created_at: string;
  contact: Partial<SearchLeadRow> | null;
  message: { id: string; status: string; send_at: string; sent_at: string | null; last_error?: string | null } | null;
}

/** Receipt totals + live message state for the progress view. */
export interface ExportProgressInfo {
  receipts: Record<string, number>;
  scheduled_remaining: number;
  sent_so_far: number;
}

/** GET /search/starters — curated seed definition (P4 starter searches). */
export interface StarterSearch {
  key: string;
  name: string;
  /** Therapeutic-area chip: Metabolic | Cardiovascular | Operational. */
  area: string;
  description: string;
  definition: SearchDefinition;
}
